import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Search, X, Upload, CheckCircle, Image, FileDown, Square, File } from 'lucide-react';
import DadosEngenheiroPanel from '../components/DadosEngenheiroPanel';
import EngenheiroViewPosVenda from '../components/EngenheiroViewPosVenda';
import FotosInstalacaoPanel from '../components/FotosInstalacaoPanel';
import FotosLocalPanel from '../components/FotosLocalPanel';
import ConversasPanel from '../components/ConversasPanel';
import CriarTarefaModal from '../components/CriarTarefaModal';
import TarefasLeadPanel from '../components/TarefasLeadPanel';

const STATUS_COLOR = {
  // lead
  pre_venda:'#64748b', aguardando_vendedor:'#94a3b8',
  visita:'#fb923c', proposta:'#f6c90e',
  fechado:'#4ade80', perdido:'#f87171', em_espera:'#f59e0b',
  // pós-venda
  aguardando:'#64748b', na_transportadora:'#fb923c', monitoramento:'#a78bfa', finalizado:'#4ade80',
  // engenheiro
  instalacao_programada:'#60a5fa', realizando_instalacao:'#f59e0b', instalacao_concluida:'#4ade80',
};
const STATUS_OPTIONS = [
  { v:'aguardando_vendedor', l:'Aguardando Vendedor' },
  { v:'visita', l:'Visita Agendada' },
  { v:'proposta', l:'Proposta Enviada' },
  { v:'fechado', l:'Venda Fechada' },
  { v:'perdido', l:'Desistiu da Compra' },
  { v:'em_espera', l:'Aguardando Cliente' },
];

const POS_VENDA_STATUS = [{ v:'aguardando', l:'Aguardando Pós Venda' }, { v:'na_transportadora', l:'Na Transportadora' }, { v:'processo_entrega', l:'Processo de Entrega' }, { v:'entregue', l:'Entregue' }, { v:'instalando', l:'Instalando' }, { v:'monitoramento', l:'Monitoramento' }, { v:'finalizado', l:'Finalizado' }];
const INSTALADOR_STATUS = [
  { v:'aguardando_instalador',  l:'Aguardando Instalador' },
  { v:'instalacao_programada',  l:'Instalação Programada' },
  { v:'instalando',             l:'Instalando' },
  { v:'instalacao_concluida',   l:'Instalação Concluída' },
];
const ENG_STATUS = [
  { v:'aguardando_engenheiro',         l:'Aguardando Engenheiro' },
  { v:'a_pagar_crt',                   l:'A Pagar CRT' },
  { v:'analise_cemig',                 l:'Análise CEMIG' },
  { v:'parecer_aprovado',              l:'Parecer Aprovado' },
  { v:'parecer_reprovado',             l:'Parecer Reprovado' },
  { v:'aguardando_solicitar_vistoria', l:'Aguardando Solicitar Vistoria' },
  { v:'vistoria_solicitada',           l:'Vistoria Solicitada' },
  { v:'vistoria_concluida',            l:'Vistoria Concluída' },
];

const S = {
  heading: { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub: { fontSize:13, color:'#475569', marginBottom:24 },
  toolbar: { display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' },
  searchBox: { display:'flex', alignItems:'center', gap:8, background:'#0e1420', border:'1px solid #1a2035', borderRadius:8, padding:'8px 12px', flex:1, maxWidth:300 },
  searchInput: { background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 },
  select: { padding:'8px 12px', borderRadius:8, background:'#0e1420', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none' },
  th: { padding:'10px 14px', textAlign:'left', fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035', whiteSpace:'nowrap', position:'sticky', top:0, background:'#0e1420', zIndex:1 },
  td: { padding:'11px 14px', fontSize:13, color:'#cbd5e1', borderBottom:'1px solid #0f1117', whiteSpace:'nowrap' },
  tag: (c) => ({ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:`${c}22`, color:c, border:`1px solid ${c}44` }),
  btn: (c='#f6c90e') => ({ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:7, background:`${c}22`, color:c, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${c}44` }),
  // Edit modal
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 },
  modal: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:16, width:'min(700px,100%)', maxHeight:'92vh', overflowY:'auto', padding:28 },
  modalTitle: { fontSize:17, fontWeight:700, color:'#f6c90e', marginBottom:20 },
  sec: { fontSize:11, color:'#f6c90e', textTransform:'uppercase', letterSpacing:1.5, fontWeight:700, marginBottom:10, marginTop:18, borderBottom:'1px solid #1a2035', paddingBottom:6 },
  row2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  row3: { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 },
  label: { fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:5, display:'block' },
  input: { width:'100%', padding:'9px 11px', borderRadius:7, background:'#0f1117', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', marginBottom:12 },
  inputSel: { width:'100%', padding:'9px 11px', borderRadius:7, background:'#0f1117', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', marginBottom:12 },
  infoRow: { display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #0f1117' },
  infoLabel: { fontSize:12, color:'#475569' },
  infoVal: { fontSize:13, color:'#cbd5e1', textAlign:'right', maxWidth:'60%' },
};

// Helpers para label de status por contexto (evita 'Instalando Placas' no pós-venda)
const LEGACY_ENG_LABEL = {
  aguardando_engenheiro:'Aguardando Engenheiro',
  a_pagar_crt:'A Pagar CRT',
  analise_cemig:'Análise CEMIG',
  parecer_aprovado:'Parecer Aprovado',
  parecer_reprovado:'Parecer Reprovado',
  aguardando_solicitar_vistoria:'Aguardando Solicitar Vistoria',
  vistoria_solicitada:'Vistoria Solicitada',
  vistoria_concluida:'Vistoria Concluída',
  // legado
  realizando_planejamento:'Realizando Planejamento',
  aguardando_cemig:'Aguardando CEMIG',
  documentacao_aprovada:'Documentação Aprovada',
  vistoria_feita:'Vistoria Concluída',
};
const labelStatus = (st, context) => {
  if (context === 'pos_venda') return POS_VENDA_STATUS.find(s => s.v === st)?.l || st;
  if (context === 'engenheiro') return LEGACY_ENG_LABEL[st] || st;
  if (context === 'instalador') return INSTALADOR_STATUS.find(s => s.v === st)?.l || st;
  return STATUS_LABEL_LEAD[st] || STATUS_OPTIONS.concat(POS_VENDA_STATUS).concat(ENG_STATUS).find(s => s.v === st)?.l || st;
};

const STATUS_LABEL_INSTALADOR = { aguardando_instalador:'Aguardando Instalador', instalacao_programada:'Instalação Programada', instalando:'Instalando', instalacao_concluida:'Instalação Concluída' };
const STATUS_LABEL_POS_VENDA  = { aguardando:'Aguardando Pós Venda', na_transportadora:'Na Transportadora', processo_entrega:'Processo de Entrega', entregue:'Entregue', instalando:'Instalando', monitoramento:'Monitoramento', finalizado:'Finalizado' };
const STATUS_LABEL_ENG        = {
  aguardando_engenheiro:'Aguardando Engenheiro', realizando_planejamento:'Realizando Planejamento',
  aguardando_cemig:'Aguardando CEMIG', documentacao_aprovada:'Documentação Aprovada', vistoria_feita:'Vistoria Feita',
  // legado — valores antigos no banco antes da migration 0008
  aguardando:'Aguardando Engenheiro', instalacao_programada:'Realizando Planejamento',
  realizando_instalacao:'Aguardando CEMIG', instalacao_concluida:'Vistoria Feita',
};
const STATUS_LABEL_FIN        = { aguardando:'Aguardando Financeiro', aguardando_pagamento:'Aguardando Pagamento', pago:'Pago' };
const STATUS_LABEL_LEAD       = { pre_venda:'Pré Venda', aguardando_vendedor:'Aguardando Vendedor', visita:'Visita Agendada', proposta:'Proposta Enviada', fechado:'Venda Fechada', perdido:'Desistiu da Compra', em_espera:'Aguardando Cliente' };

const STATUS_COLOR_EXT = {
  ...STATUS_COLOR,
  aguardando:'#64748b', aguardando_pagamento:'#60a5fa', pago:'#4ade80',
  na_transportadora:'#fb923c', processo_entrega:'#facc15', entregue:'#22d3ee', finalizado:'#4ade80',
  // engenheiro (novos)
  aguardando_engenheiro:'#64748b', realizando_planejamento:'#60a5fa',
  aguardando_cemig:'#fbbf24', documentacao_aprovada:'#a78bfa', vistoria_feita:'#4ade80',
};

// Formata data+hora a partir de string ISO ou datetime-local (evita Invalid Date)
const fmtDT = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
};
// Formata apenas data (para campos DateField — adiciona T12h para evitar shift de fuso)
const fmtD = (v) => {
  if (!v) return '—';
  const d = new Date(v.length <= 10 ? v + 'T12:00:00' : v);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('pt-BR');
};


/* ── KitTableModal: tabela completa de kits com "Escolher este" ─────────── */
function KitTableModal({ kits, onChoose, onClose, clienteNome }) {
  const [search, setSearch] = React.useState('');
  const filtered = search.trim()
    ? kits.filter(k =>
        k.equipamento.toLowerCase().includes(search.toLowerCase()) ||
        String(k.geracao).includes(search)
      )
    : kits;

  const fmt = (v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1200,
                  display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:14,
                    padding:24, width:680, maxWidth:'96vw', maxHeight:'85vh',
                    display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#f6c90e' }}>Selecionar Kit Solar</div>
            {clienteNome && <div style={{ fontSize:12, color:'#475569', marginTop:2 }}>Cliente: {clienteNome}</div>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', padding:4 }}>
            <X size={18}/>
          </button>
        </div>

        {/* Search */}
        <div style={{ display:'flex', alignItems:'center', gap:8, background:'#080c14',
                      border:'1px solid #1a2035', borderRadius:8, padding:'7px 12px', marginBottom:14 }}>
          <Search size={14} color="#475569"/>
          <input
            style={{ background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 }}
            placeholder="Buscar equipamento ou geração kWh..." value={search}
            onChange={e => setSearch(e.target.value)} autoFocus />
        </div>

        {/* Tabela */}
        <div style={{ flex:1, overflowY:'auto', overflowX:'auto' }}>
          {kits.length === 0 ? (
            <div style={{ textAlign:'center', padding:32, color:'#475569' }}>Carregando kits...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:32, color:'#475569' }}>Nenhum kit encontrado</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  {['Equipamento','Geração','Placas','Qtd','Valor',''].map(h => (
                    <th key={h} style={{ padding:'8px 12px', fontSize:11, color:'#475569',
                                        textTransform:'uppercase', letterSpacing:0.8,
                                        borderBottom:'1px solid #1a2035', textAlign:'left',
                                        whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(k => (
                  <tr key={k.id}
                    style={{ cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background='#111827'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={{ padding:'10px 12px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#e2e8f0', fontWeight:500 }}>
                      {k.equipamento}
                    </td>
                    <td style={{ padding:'10px 12px', borderBottom:'1px solid #0f1117', fontSize:14, color:'#f6c90e', fontWeight:700 }}>
                      {k.geracao} kWh
                    </td>
                    <td style={{ padding:'10px 12px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#94a3b8' }}>
                      {k.placas_w}W
                    </td>
                    <td style={{ padding:'10px 12px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#94a3b8' }}>
                      {k.quantidade}x
                    </td>
                    <td style={{ padding:'10px 12px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#4ade80', fontWeight:600 }}>
                      R$ {fmt(k.valor)}
                    </td>
                    <td style={{ padding:'10px 12px', borderBottom:'1px solid #0f1117' }}>
                      <button
                        onClick={() => onChoose(k.id, k)}
                        style={{ background:'rgba(246,201,14,0.15)', border:'1px solid rgba(246,201,14,0.4)',
                                 color:'#f6c90e', borderRadius:8, padding:'5px 12px',
                                 fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                        Escolher este
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ marginTop:14, display:'flex', justifyContent:'flex-end' }}>
          <button onClick={onClose}
            style={{ background:'#1a2035', border:'none', color:'#94a3b8', borderRadius:8,
                     padding:'8px 18px', fontSize:13, cursor:'pointer' }}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function FotosInstalModal({ fotos, onClose }) {
  const [idx, setIdx] = React.useState(0);
  if (!fotos || fotos.length === 0) return null;
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ position:'relative', maxWidth:'90vw' }}>
        <button onClick={onClose} style={{ position:'absolute', top:-40, right:0, background:'none', border:'none', color:'#fff', fontSize:26, cursor:'pointer', lineHeight:1 }}>✕</button>
        <img src={fotos[idx].url} alt={fotos[idx].nome}
          style={{ maxWidth:'82vw', maxHeight:'80vh', borderRadius:10, objectFit:'contain', display:'block' }} />
        {fotos.length > 1 && (
          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:14, marginTop:12 }}>
            <button onClick={() => setIdx(i => (i - 1 + fotos.length) % fotos.length)}
              style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:6, padding:'6px 16px', cursor:'pointer', fontSize:18 }}>‹</button>
            <span style={{ color:'#94a3b8', fontSize:13 }}>{idx + 1} / {fotos.length}</span>
            <button onClick={() => setIdx(i => (i + 1) % fotos.length)}
              style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:6, padding:'6px 16px', cursor:'pointer', fontSize:18 }}>›</button>
          </div>
        )}
        <div style={{ textAlign:'center', color:'#64748b', fontSize:12, marginTop:8 }}>{fotos[idx].nome}</div>
      </div>
    </div>
  );
}

/* ---- PropostaVendaModal (4 etapas no modal de venda fechada) — até 3 kits ---- */
function PropostaVendaModal({ lead, kitsExternos, onSuccess, onClose }) {
  const { isChefe } = useAuth();
  const fmtBRL = (v) => Number(v).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
  const MAX_KITS = 3;

  const [kits, setKits] = useState(kitsExternos || []);
  const [kitsSearch, setKitsSearch] = useState('');

  const candidatosIniciais = (lead.kits_propostos && lead.kits_propostos.length > 0)
    ? lead.kits_propostos.map(c => ({ id: c.kit_id, equipamento: c.kit_equipamento, marca: c.kit_marca, geracao: c.kit_geracao, valor: c.kit_valor, placas_w: c.kit_placas_w, quantidade: c.kit_quantidade }))
    : (lead.kit ? [{ id: lead.kit, equipamento: lead.kit_equipamento, marca: lead.kit_marca, geracao: lead.kit_geracao, valor: lead.kit_valor, placas_w: lead.kit_placas_w, quantidade: lead.kit_quantidade }] : []);
  const valoresIniciais = {};
  (lead.kits_propostos || []).forEach(c => {
    if (c.valor_proposto) valoresIniciais[c.kit_id] = String(Number(c.valor_proposto).toFixed(2)).replace('.', ',');
  });

  const [selectedKits, setSelectedKits] = useState(candidatosIniciais);
  const [valoresPorKit, setValoresPorKit] = useState(valoresIniciais);
  const [forma, setForma] = useState(lead.forma_pagamento || '');
  const [step, setStep] = useState(1);
  const [custoSemStr, setCustoSemStr] = useState('');
  const [custoComStr, setCustoComStr] = useState('');
  const [reajusteStr, setReajusteStr] = useState('10');
  const [gerando, setGerando] = useState(false);
  const [coeficientes, setCoeficientes] = useState([]);
  const [selectedParcela, setSelectedParcela] = useState(
    lead.cartao_parcelas ? { parcela: lead.cartao_parcelas, coeficiente: null } : null
  );

  const comissaoPct = lead.vendedor_comissao_pct || 0;

  const valorNumFor = (kitId) => {
    const s = valoresPorKit[kitId] || '';
    return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
  };

  useEffect(() => {
    if (!kitsExternos || kitsExternos.length === 0) api.get('/kits/').then(r => setKits(r.data)).catch(() => {});
    api.get('/coeficientes-parcela/').then(r => setCoeficientes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Ao entrar nos kits, pre-popula valor sugerido (valor de tabela) quando ainda vazio
    setValoresPorKit(prev => {
      const next = { ...prev };
      selectedKits.forEach(k => {
        if (!next[k.id] && k.valor) next[k.id] = Number(k.valor).toFixed(2).replace('.', ',');
      });
      return next;
    });
  }, [selectedKits]);

  const kitsFiltrados = kitsSearch.trim()
    ? kits.filter(k => k.equipamento.toLowerCase().includes(kitsSearch.toLowerCase()) || String(k.geracao).includes(kitsSearch))
    : kits;

  const toggleKit = (k) => {
    setSelectedKits(prev => {
      const exists = prev.find(p => p.id === k.id);
      if (exists) return prev.filter(p => p.id !== k.id);
      if (prev.length >= MAX_KITS) { alert(`Você pode selecionar no máximo ${MAX_KITS} kits.`); return prev; }
      return [...prev, k];
    });
  };

  const step1Ok = selectedKits.length >= 1 && selectedKits.length <= MAX_KITS;
  const step2Ok = selectedKits.every(k => valorNumFor(k.id) > 0);
  const step3Ok = forma && (forma !== 'Cartão' ? true : selectedParcela != null);
  const custoSemNum = parseFloat(custoSemStr.replace(/\./g, '').replace(',', '.')) || 0;
  const custoComNum = parseFloat(custoComStr.replace(/\./g, '').replace(',', '.')) || 0;
  const reajusteNum = parseFloat(reajusteStr.replace(',', '.')) || 10;
  const step4Ok = custoSemNum > 0 && custoComNum > 0;

  const handleGerar = async () => {
    setGerando(true);
    // Pré-abrir janela ANTES do await — iOS Safari bloqueia window.open em contexto assíncrono.
    // Declarar fora do try para acessar no catch e fechar em caso de erro.
    let win = null;
    try { win = window.open('', '_blank'); } catch (_) {}
    try {
      const patch = { forma_pagamento: forma };
      if (forma === 'Cartão' && selectedParcela) {
        const valorBase = valorNumFor(selectedKits[0].id);
        const coef = parseFloat(selectedParcela.coeficiente);
        const totalComTaxa = parseFloat((valorBase * coef).toFixed(2));
        patch.valor_total_com_taxa = totalComTaxa;
        patch.cartao_parcelas = selectedParcela.parcela;
        patch.cartao_valor_parcela = parseFloat((totalComTaxa / selectedParcela.parcela).toFixed(2));
      }
      await api.patch('/leads/' + lead.id + '/', patch);
      const kitsBody = selectedKits.map(k => ({ kit_id: k.id, valor: valorNumFor(k.id) }));
      const resp = await api.post('/documentos/gerar-proposta/' + lead.id + '/', { kits: kitsBody, custo_sem: custoSemNum, custo_com: custoComNum, reajuste_anual: reajusteNum }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type:'application/pdf' }));
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        const a = document.createElement('a'); a.href = url; a.download = 'proposta_' + lead.id + '.pdf'; a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      const kitsPropostosResultado = selectedKits.map((k, i) => ({
        ordem: i + 1, kit_id: k.id, kit_equipamento: k.equipamento, kit_marca: k.marca,
        kit_geracao: k.geracao, kit_valor: k.valor, valor_proposto: valorNumFor(k.id),
      }));
      onSuccess({ proposta_gerada: true, kits_propostos: kitsPropostosResultado, forma_pagamento: forma });
      onClose();
    } catch (e) {
      let msg = e.message || 'tente novamente';
      if (e.response) {
        let data = e.response.data;
        if (data instanceof Blob) {
          try { data = JSON.parse(await data.text()); } catch (_) { data = null; }
        }
        msg = data?.error || data?.erro || msg;
      }
      if (win && !win.closed) win.close();
      alert('Erro ao gerar proposta: ' + msg);
    }
    finally { setGerando(false); }
  };

  const overlay  = { position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center' };
  const box      = { background:'#0e1420', border:'1px solid #1a2035', borderRadius:14, padding:24, width:620, maxWidth:'95vw', maxHeight:'88vh', display:'flex', flexDirection:'column' };
  const stepBtn  = (active) => ({ width:28, height:28, borderRadius:'50%', border:'none', cursor:'default', fontWeight:700, fontSize:13, background: active ? '#f6c90e' : '#1a2035', color: active ? '#080c14' : '#475569', display:'inline-flex', alignItems:'center', justifyContent:'center' });
  const nextBtn  = (ok) => ({ background: ok ? '#f6c90e' : '#1a2035', color: ok ? '#080c14' : '#475569', border:'none', borderRadius:8, padding:'9px 22px', fontWeight:700, fontSize:13, cursor: ok ? 'pointer' : 'not-allowed' });
  const inputS   = { background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'9px 13px', color:'#e2e8f0', fontSize:14, outline:'none', width:'100%', boxSizing:'border-box' };
  const lbl      = { fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:6, display:'block' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        {/* header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#f6c90e' }}>Gerar Proposta</div>
            <div style={{ fontSize:12, color:'#475569', marginTop:2 }}>{lead.cliente_nome}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer' }}><X size={18}/></button>
        </div>
        {/* steps indicator */}
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:20 }}>
          {[['1','Kit Solar'],['2','Valor'],['3','Pagamento'],['4','Análise']].map(([n,label],i) => (
            <React.Fragment key={n}>
              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                <div style={stepBtn(step === i+1)}>{n}</div>
                <span style={{ fontSize:11, color: step === i+1 ? '#e2e8f0' : '#475569', fontWeight: step === i+1 ? 600 : 400 }}>{label}</span>
              </div>
              {i < 3 && <div style={{ flex:1, height:1, background:'#1a2035' }}/>}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: KIT (até 3 selecionáveis) */}
        {step === 1 && (
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:10 }}>Escolha de 1 a {MAX_KITS} kits solares para esta proposta:</div>
            {selectedKits.length > 0 && (
              <div style={{ background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:8,
                            padding:'9px 14px', marginBottom:10, fontSize:13, color:'#4ade80', fontWeight:600 }}>
                ✅ {selectedKits.length} {selectedKits.length === 1 ? 'kit selecionado' : 'kits selecionados'}
              </div>
            )}
            <div style={{ display:'flex', alignItems:'center', gap:8, background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'7px 12px', marginBottom:10 }}>
              <Search size={13} color="#475569"/>
              <input style={{ background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 }}
                placeholder="Buscar equipamento ou geração..." value={kitsSearch} onChange={e => setKitsSearch(e.target.value)} autoFocus />
            </div>
            <div style={{ overflowY:'auto', overflowX:'auto', maxHeight:300, border:'1px solid #1a2035', borderRadius:8 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ position:'sticky', top:0, background:'#0e1420', zIndex:10 }}>
                    {['Marca','Equipamento','Qtd Inversores','Ger. (kWh)','Placas','Pot. Total','Valor',''].map(h => (
                      <th key={h} style={{ padding:'8px 10px', fontSize:10, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035', textAlign:'left', background:'#0e1420' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kitsFiltrados.map(k => {
                    const idx = selectedKits.findIndex(p => p.id === k.id);
                    const isSelected = idx !== -1;
                    return (
                      <tr key={k.id} style={{ background: isSelected ? 'rgba(246,201,14,0.1)' : 'transparent', cursor:'pointer' }}
                        onClick={() => toggleKit(k)}>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#a78bfa', fontWeight:600 }}>{k.marca || '—'}</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#e2e8f0', fontWeight:600 }}>
                          {isSelected && <span style={{ color:'#f6c90e', marginRight:6 }}>Opção {idx + 1}</span>}
                          {k.equipamento}
                        </td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#94a3b8' }}>{k.qtd_inversores ?? '—'}</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#f6c90e', fontWeight:700 }}>{k.geracao}</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#94a3b8' }}>{k.quantidade}x {k.placas_w}W</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#60a5fa', fontWeight:600 }}>{((k.placas_w * k.quantidade) / 1000).toFixed(2)} kWp</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#4ade80', fontWeight:600 }}>{fmtBRL(k.valor)}</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117' }}>
                          <button onClick={e => { e.stopPropagation(); toggleKit(k); }}
                            style={{ background: isSelected ? 'transparent' : '#f6c90e', color: isSelected ? '#f87171' : '#080c14',
                                     border: isSelected ? '1px solid rgba(248,113,113,0.4)' : 'none', borderRadius:6, padding:'5px 12px',
                                     fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                            {isSelected ? 'Remover' : 'Escolher Este'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {kitsFiltrados.length === 0 && (
                    <tr><td colSpan={8} style={{ padding:24, textAlign:'center', color:'#475569' }}>Nenhum kit encontrado</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* STEP 2: VALOR (um campo por kit selecionado) */}
        {step === 2 && (
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            {selectedKits.map((k, i) => {
              const kitValor = k.valor ? Number(k.valor) : 0;
              const valorNum = valorNumFor(k.id);
              const limiteDesconto = kitValor > 0 ? kitValor * 0.95 : 0;
              const temDesconto = !isChefe() && !isGerenteComercial() && kitValor > 0 && valorNum > 0 && valorNum < limiteDesconto;
              const pctDesconto = kitValor > 0 && valorNum > 0 ? ((1 - valorNum / kitValor) * 100) : 0;
              const comissaoVal = comissaoPct > 0 ? valorNum * comissaoPct / 100 : null;
              return (
                <div key={k.id} style={{ marginBottom: i < selectedKits.length - 1 ? 22 : 0, paddingBottom: i < selectedKits.length - 1 ? 18 : 0, borderBottom: i < selectedKits.length - 1 ? '1px solid #1a2035' : 'none' }}>
                  {selectedKits.length > 1 && (
                    <div style={{ fontSize:11, color:'#f6c90e', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Opção {i + 1} — {k.equipamento}</div>
                  )}
                  {kitValor > 0 && (
                    <div style={{ fontSize:12, color:'#64748b', marginBottom:8 }}>
                      💡 Valor do kit {selectedKits.length > 1 ? i + 1 : ''}: <strong style={{ color:'#e2e8f0' }}>{fmtBRL(kitValor)}</strong>
                      {!isChefe() && !isGerenteComercial() && <span style={{ color:'#94a3b8' }}> · Abaixo de 5%, a proposta será revisada pelo chefe</span>}
                    </div>
                  )}
                  <label style={lbl}>Valor total da venda (R$)</label>
                  <input style={{ ...inputS, fontSize:20, fontWeight:700, color: temDesconto ? '#f87171' : '#f6c90e', marginBottom:12 }}
                    type="text" placeholder="Ex: 37.555,00" value={valoresPorKit[k.id] || ''}
                    onChange={e => setValoresPorKit(v => ({ ...v, [k.id]: e.target.value.replace(/[^0-9,]/g, '') }))}
                    autoFocus={i === 0} />
                  {temDesconto ? (
                    <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.35)', borderRadius:10, padding:'12px 16px' }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#f87171', marginBottom:4 }}>⚠️ Desconto acima de 5%</div>
                      <div style={{ fontSize:13, color:'#fca5a5' }}>Valor é <strong>{pctDesconto.toFixed(2)}%</strong> abaixo do kit ({fmtBRL(kitValor)}). O chefe revisará.</div>
                    </div>
                  ) : comissaoVal !== null && valorNum > 0 && !isChefe() && (
                    <div style={{ background:'rgba(74,222,128,0.07)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:'12px 16px' }}>
                      <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:4 }}>Sua comissão estimada</div>
                      <div style={{ fontSize:20, fontWeight:800, color:'#4ade80' }}>{fmtBRL(comissaoVal)}</div>
                      <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{comissaoPct}% sobre {fmtBRL(valorNum)}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* STEP 3: FORMA DE PAGAMENTO */}
        {step === 3 && (
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            <label style={lbl}>Forma de Pagamento</label>
            <select style={{ ...inputS, marginBottom:16 }} value={forma} onChange={e => { setForma(e.target.value); setSelectedParcela(null); }} autoFocus>
              <option value="">— Selecione —</option>
              <option>A Vista</option>
              <option>Financiamento</option>
              <option value="Cartão">Cartão</option>
              <option>Boleto</option>
            </select>
            {forma === 'Cartão' && (() => {
              const valorBase = selectedKits.length > 0 ? valorNumFor(selectedKits[0].id) : 0;
              return (
              <div style={{ background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.18)', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:12, color:'#64748b', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>
                  Parcelas {selectedKits.length > 1 ? '(prévia com base na Opção 1)' : ''}:
                </div>
                <div style={{ overflowY:'auto', overflowX:'auto', maxHeight:220, border:'1px solid #1a2035', borderRadius:8 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        {['Parcelas','Juros','Valor/parcela','Total com taxa',''].map(h => (
                          <th key={h} style={{ padding:'7px 10px', fontSize:10, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035', textAlign:'left', background:'#0e1420', position:'sticky', top:0 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {coeficientes.map(item => {
                        const coef = parseFloat(item.coeficiente);
                        const totalCom = valorBase > 0 ? valorBase * coef : 0;
                        const parcelaCom = totalCom / item.parcela;
                        const juros = ((coef - 1) * 100).toLocaleString('pt-BR', { minimumFractionDigits:2, maximumFractionDigits:2 }) + '%';
                        const isSelected = selectedParcela?.parcela === item.parcela;
                        return (
                          <tr key={item.id} style={{ background: isSelected ? 'rgba(246,201,14,0.1)' : 'transparent', cursor:'pointer' }}
                            onClick={() => setSelectedParcela(item)}>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:14, fontWeight:800, color:'#f6c90e' }}>{item.parcela}x</td>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#60a5fa', fontWeight:700 }}>{juros}</td>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#f6c90e', fontWeight:700 }}>{valorBase > 0 ? fmtBRL(parcelaCom) : '—'}</td>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#4ade80', fontWeight:700 }}>{valorBase > 0 ? fmtBRL(totalCom) : '—'}</td>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#4ade80' }}>{isSelected && '✓'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })()}
            <div style={{ background:'#080c14', border:'1px solid #1a2035', borderRadius:10, padding:'12px 16px', marginTop:16 }}>
              <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:8 }}>Resumo</div>
              {selectedKits.map((k, i) => (
                <div key={k.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 16px', fontSize:13, marginBottom: i < selectedKits.length - 1 ? 8 : 0 }}>
                  <div style={{ color:'#475569' }}>{selectedKits.length > 1 ? `Kit (Opção ${i + 1}):` : 'Kit:'}</div>
                  <div style={{ color:'#e2e8f0', fontWeight:600 }}>{k.equipamento || '—'}</div>
                  <div style={{ color:'#475569' }}>Qtd Inversores:</div>
                  <div style={{ color:'#e2e8f0' }}>{k.qtd_inversores ?? '—'}</div>
                  <div style={{ color:'#475569' }}>Valor:</div>
                  <div style={{ color:'#f6c90e', fontWeight:700 }}>{valorNumFor(k.id) > 0 ? fmtBRL(valorNumFor(k.id)) : '—'}</div>
                </div>
              ))}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 16px', fontSize:13, marginTop:8 }}>
                <div style={{ color:'#475569' }}>Pagamento:</div>
                <div style={{ color:'#e2e8f0' }}>{forma || '—'}</div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: ANÁLISE FINANCEIRA */}
        {step === 4 && (
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>Informe os valores de energia para calcular o retorno do investimento.</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
              <div>
                <label style={lbl}>Custo mensal SEM sistema (R$)</label>
                <input style={{ ...inputS, fontSize:16, fontWeight:700, color:'#ef4444' }} type="text" placeholder="Ex: 850,00"
                  value={custoSemStr} onChange={e => setCustoSemStr(e.target.value.replace(/[^0-9,]/g, ''))} autoFocus />
              </div>
              <div>
                <label style={lbl}>Custo mensal COM sistema (R$)</label>
                <input style={{ ...inputS, fontSize:16, fontWeight:700, color:'#4ade80' }} type="text" placeholder="Ex: 95,00"
                  value={custoComStr} onChange={e => setCustoComStr(e.target.value.replace(/[^0-9,]/g, ''))} />
              </div>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={lbl}>Reajuste anual de energia (%)</label>
              <input style={{ ...inputS, width:160 }} type="text" placeholder="10" value={reajusteStr}
                onChange={e => setReajusteStr(e.target.value.replace(/[^0-9,.]/g, ''))} />
              <div style={{ fontSize:11, color:'#475569', marginTop:5 }}>Padrão: 10% ao ano (média histórica ANEEL)</div>
            </div>
            {custoSemNum > 0 && custoComNum > 0 && (
              <div style={{ background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.18)', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:10 }}>Prévia dos cálculos</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 20px', fontSize:13 }}>
                  <div style={{ color:'#475569' }}>Economia mensal:</div>
                  <div style={{ color:'#4ade80', fontWeight:700 }}>{(custoSemNum - custoComNum).toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}</div>
                  <div style={{ color:'#475569' }}>Economia anual:</div>
                  <div style={{ color:'#4ade80', fontWeight:700 }}>{((custoSemNum - custoComNum) * 12).toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}</div>
                  {selectedKits.map((k, i) => {
                    const v = valorNumFor(k.id);
                    return (
                      <React.Fragment key={k.id}>
                        <div style={{ color:'#475569' }}>Payback {selectedKits.length > 1 ? `(Opção ${i + 1})` : 'estimado'}:</div>
                        <div style={{ color:'#f6c90e', fontWeight:700 }}>{v > 0 ? `≈ ${(v / ((custoSemNum - custoComNum) * 12)).toFixed(1)} anos` : '—'}</div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* footer */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:20, paddingTop:16, borderTop:'1px solid #1a2035' }}>
          <button onClick={step === 1 ? onClose : () => setStep(s => s-1)}
            style={{ background:'transparent', border:'1px solid #1a2035', borderRadius:8, padding:'8px 18px', color:'#64748b', fontSize:13, cursor:'pointer' }}>
            {step === 1 ? 'Cancelar' : '← Voltar'}
          </button>
          {step < 4 ? (
            <button style={{ background: (step===1?step1Ok:step===2?step2Ok:step3Ok) ? '#f6c90e' : '#1a2035', color: (step===1?step1Ok:step===2?step2Ok:step3Ok) ? '#080c14' : '#475569', border:'none', borderRadius:8, padding:'9px 22px', fontWeight:700, fontSize:13, cursor: (step===1?step1Ok:step===2?step2Ok:step3Ok) ? 'pointer' : 'not-allowed' }}
              disabled={step===1?!step1Ok:step===2?!step2Ok:!step3Ok}
              onClick={() => setStep(s => s+1)}>
              Próximo →
            </button>
          ) : (
            <button style={{ background: step4Ok && !gerando ? '#f6c90e' : '#1a2035', color: step4Ok && !gerando ? '#080c14' : '#475569', border:'none', borderRadius:8, padding:'9px 22px', fontWeight:700, fontSize:13, cursor: step4Ok && !gerando ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', gap:6 }}
              disabled={!step4Ok || gerando} onClick={handleGerar}>
              <FileDown size={14}/>{gerando ? 'Gerando...' : 'Confirmar e Gerar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Clientes() {
  const { user, isChefe, isVendedor, isGerenteComercial, isPreVenda, isPosVenda, isEngenheiro, isInstalador } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [meusClientes, setMeusClientes] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clienteForm, setClienteForm] = useState({});
  const [savingCliente, setSavingCliente] = useState(false);
  const [mostrarCriarTarefa, setMostrarCriarTarefa] = useState(false);
  const [editingInstalador, setEditingInstalador] = useState(null); // registro Instalador do lead
  const [fotosInstalModal, setFotosInstalModal] = useState(null);
  const [confirmarPrevisao, setConfirmarPrevisao] = useState(null); // null | 'confirmado' | 'alterar'
  const [form, setForm] = useState({});
  const [provaFile, setProvaFile] = useState(null);
  const [fotoFile, setFotoFile] = useState(null);
  // Estados de fluxo do modal vendedor
  const [aceitouProposta, setAceitouProposta] = useState('');
  const [naoAceitouOpcao, setNaoAceitouOpcao] = useState('');
  const [confirmarVendaFechada, setConfirmarVendaFechada] = useState('');
  const [kitVendaId, setKitVendaId] = useState('');       // kit selecionado ao fechar venda (entre os candidatos da proposta)
  const [kitVendaConfirmado, setKitVendaConfirmado] = useState(false); // confirmou kit dentre os candidatos
  const [kitVendaKitObj, setKitVendaKitObj] = useState(null); // kit completo, quando escolhido na tabela (lead sem candidatos)
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [instaladoresList, setInstaladores] = useState([]);   // pessoas instaladoras (para dropdown)
  const [instaladorJobs, setInstaladorJobs] = useState([]);   // registros de job (para encontrar o record do lead)
  // Download pós obra
  const [gerandoPosObra, setGerandoPosObra] = useState(false);
  // Seção Ver Documentos (engenheiro)
  const [showDocs, setShowDocs] = useState(false);
  // Inline vendedor edit (chefe only)
  const [vendedores, setVendedores] = useState([]);
  const [editingVendedorId, setEditingVendedorId] = useState(null);
  const [vendedorDraft, setVendedorDraft] = useState('');
  const [savingVendedor, setSavingVendedor] = useState(false);
  // Inline kit edit (chefe + vendedor)
  const [kitsLista, setKitsLista] = useState([]);
  const [kitTableModal, setKitTableModal] = useState(null); // { leadId, clienteNome, callback }
  const [editingKitId, setEditingKitId] = useState(null);
  const [kitDraft, setKitDraft] = useState('');
  const [savingKit, setSavingKit] = useState(false);
  // Página de origem (para voltar após fechar modal aberto pelo lápis do kanban)
  const [returnTo, setReturnTo] = useState(null);
  // leadId pendente de abertura (capturado do location.state antes de leads carregar)
  const [pendingLeadId, setPendingLeadId] = useState(null);
  // Voltar para o funil (chefe only)
  const [voltarFunilLead, setVoltarFunilLead] = useState(null); // lead
  const [voltandoFunil, setVoltandoFunil] = useState(false);
  // Modal Enviar para Pré Venda (chefe only)
  const [preVendaModal, setPreVendaModal] = useState(null); // lead
  const [preVendaNota, setPreVendaNota] = useState('');
  const [savingPreVenda, setSavingPreVenda] = useState(false);
  const [preVendaFuncionarios, setPreVendaFuncionarios] = useState([]);
  const [preVendaResponsavelId, setPreVendaResponsavelId] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  // Ações de status do engenheiro
  const [savingEngStatus, setSavingEngStatus] = useState(false);
  const [engStatusError, setEngStatusError] = useState('');
  // Upload de boleto
  const [uploadingBoleto, setUploadingBoleto] = useState(false);
  const [boletoFile, setBoletoFile] = useState(null);
  const [boletoError, setBoletoError] = useState('');
  const conversasPanelRef = useRef(null);
  const boletoInputRef = useRef(null);
  // Upload de CRT
  const [uploadingCrt, setUploadingCrt] = useState(false);
  const [crtFile, setCrtFile] = useState(null);
  const [crtError, setCrtError] = useState('');
  const crtInputRef = useRef(null);
  // Modal Gerar Proposta (venda fechada)
  const [propostaVendaModal, setPropostaVendaModal] = useState(false);
  const [gerandoCemig, setGerandoCemig] = useState(false);
  const [gerandoContrato, setGerandoContrato] = useState(false);
  const [loadErro, setLoadErro] = useState('');
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Reseta o modal de criar tarefa ao fechar o modal de edição
  useEffect(() => { if (!editing) setMostrarCriarTarefa(false); }, [editing]);

  const fetch = () => {
    const endpoint = isPosVenda() ? '/pos-venda/'
      : isEngenheiro()  ? '/engenheiro/'
      : isInstalador()  ? '/instalador/'
      : '/leads/';
    const params = ((isChefe() || isGerenteComercial()) && meusClientes) ? '?meus=true' : '';
    setLoadErro('');
    api.get(endpoint + params).then(r => setLeads(r.data)).catch(() => setLoadErro('Erro ao carregar clientes. Tente atualizar a página.'));
  };

  useEffect(() => {
    fetch();
    if (isPosVenda() || isChefe()) {
      // Lista de pessoas instaladoras (para dropdown de seleção)
      api.get('/instaladores/').then(r => setInstaladores(r.data)).catch(() => {});
      // Registros de jobs instalador (para encontrar o record vinculado a cada lead)
      api.get('/instalador/').then(r => setInstaladorJobs(r.data)).catch(() => {});
    }
    if (isChefe() || isGerenteComercial()) {
      // Carrega vendedores para o inline-edit de reassign
      api.get('/usuarios/').then(r => {
        const us = r.data;
        setVendedores(us.filter(u => (u.perfil_acesso === 1 || u.perfil_acesso === 2 || u.perfil_acesso === 3) && u.ativo !== false));
        setPreVendaFuncionarios(us.filter(u => u.perfil_acesso === 4 && u.ativo !== false));
      }).catch(() => {});
    }
    if (isChefe() || isVendedor() || isGerenteComercial()) {
      api.get('/kits/').then(r => setKitsLista(r.data)).catch(() => {});
    }
  }, []);

  // Re-busca quando chefe alterna filtro de clientes
  useEffect(() => {
    if (isChefe() || isGerenteComercial()) fetch();
  }, [meusClientes]);

  // Effect 1: captura leadId do location.state imediatamente e limpa o state.
  // Não depende de `leads` — só roda quando location.state muda.
  useEffect(() => {
    if (location.state?.leadId) {
      if (location.state?.from) setReturnTo(location.state.from);
      setPendingLeadId(location.state.leadId);
      // Limpa o state para que navegar de volta não reabra o modal
      navigate(location.pathname, { state: {}, replace: true });
    }
  }, [location.state]); // eslint-disable-line

  // Effect 2: abre o modal quando pendingLeadId e leads estiverem disponíveis.
  // Aguarda o carregamento dos leads antes de tentar o find.
  useEffect(() => {
    if (!pendingLeadId || !leads.length) return;
    const lead = leads.find(l => (l.id === pendingLeadId || l.lead === pendingLeadId));
    if (lead) {
      const st = lead.status;
      const locked = isInstalador() ? st === 'instalacao_concluida'
        : isEngenheiro() ? st === 'vistoria_concluida'
        : isPosVenda()   ? st === 'finalizado'
        : false;
      if (!locked) openEdit(lead);
    }
    setPendingLeadId(null);
  }, [pendingLeadId, leads]); // eslint-disable-line

  // Fecha o modal e volta para a página anterior se veio do kanban
  const handleClose = () => {
    setEditing(null);
    if (returnTo) { const dest = returnTo; setReturnTo(null); navigate(dest); }
  };

  const filtered = leads.filter(l => {
    // Chefe/Gerente não vê leads em pré-venda (eles estão no Kanban de Pré Venda)
    if ((isChefe() || isGerenteComercial()) && l.status === 'pre_venda') return false;
    const name = (l.cliente_nome || '').toLowerCase();
    const city = (l.cidade || l.cliente_cidade || '').toLowerCase();
    const matchSearch = name.includes(search.toLowerCase()) || city.includes(search.toLowerCase());
    const matchStatus = !statusFilter || l.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const openEdit = (item) => {
    setClienteForm({
      nome: item.cliente_nome || '',
      telefone: item.cliente_telefone || '',
      email: item.cliente_email || '',
      endereco: item.cliente_endereco || '',
      cidade: item.cliente_cidade || item.cidade || '',
      estado: item.cliente_estado || '',
      cpf_doc: item.cliente_cpf_doc || '',
      rg_doc: item.cliente_rg_doc || '',
      consumo_mensal: item.cliente_consumo ? String(item.cliente_consumo) : '',
    });
    let instaladorRecord = null;
    if (isPosVenda() || isChefe()) {
      // item.lead existe em PosVendaSerializer (FK para o lead); item.id é o id do registro PosVenda
      const leadId = item.lead || item.id;
      // instaladorJobs tem o campo "lead" (id do lead) — busca o job correto
      instaladorRecord = instaladorJobs.find(j => j.lead === leadId) || null;
    }
    setEditingInstalador(instaladorRecord);
    const toLocal = (v) => v ? v.slice(0, 16) : '';
    setForm({
      ...item,
      instalador_designado: instaladorRecord?.instalador ?? '',
      data_prevista_instalacao: instaladorRecord?.data_prevista || '',
      // vendedor flow fields
      entrou_em_contato: '',
      novoStatus: (item.status === 'perdido' || item.status === 'em_espera') ? item.status : '',
      data_visita: toLocal(item.data_visita),
      prazo_resposta_vendedor: toLocal(item.prazo_resposta_vendedor),
      proposta_apresentada: '',
    });
    setEditing(item);
    setProvaFile(null);
    setFotoFile(null);
    setConfirmarPrevisao(null);
    setAceitouProposta('');
    setNaoAceitouOpcao('');
    setConfirmarVendaFechada('');
    setKitVendaId('');
    setKitVendaConfirmado(false);
    setShowDocs(false);
  };

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const fb = k => e => setForm(p => ({ ...p, [k]: e.target.checked }));


  const saveClienteData = async () => {
    if (!editing?.cliente) return;
    setSavingCliente(true);
    try {
      await api.post(`/documentos/salvar-cpf-rg/${editing.cliente}/`, clienteForm);
      setToast('Dados do cliente atualizados!');
      setTimeout(() => setToast(''), 3000);
      fetch();
    } catch (e) {
      alert('Erro ao salvar dados do cliente: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSavingCliente(false); }
  };

  const voltarParaFunil = async (lead) => {
    setVoltandoFunil(true);
    try {
      await api.post(`/leads/${lead.id}/voltar-funil/`);
      setVoltarFunilLead(null);
      handleClose();
      setToast('Lead voltou para o funil comercial.');
      setTimeout(() => setToast(''), 3000);
      fetch();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.error || e.response?.data?.erro || JSON.stringify(e.response?.data) || e.message));
    } finally { setVoltandoFunil(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Deletar o lead de ${item.cliente_nome}? Esta ação não pode ser desfeita.`)) return;
    try {
      const endpoint = isInstalador() ? 'instalador' : isPosVenda() ? 'pos-venda' : isEngenheiro() ? 'engenheiro' : 'leads';
      await api.delete(`/${endpoint}/${item.id}/`);
      setToast('Registro deletado.');
      setTimeout(() => setToast(''), 3000);
      fetch();
    } catch (e) {
      alert('Erro ao deletar: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    }
  };

  const save = async () => {
    // Salva conversa pendente (se o usuário digitou mas não clicou em "Adicionar conversa")
    if (conversasPanelRef.current) {
      await conversasPanelRef.current.salvarPendente();
    }
    setSaving(true);
    try {
      if (isInstalador()) {
        const fd = new FormData();
        fd.append('status', form.status || '');
        if (form.status === 'instalacao_programada') {
          if (form.data_prevista) fd.append('data_prevista', form.data_prevista);
        } else if (form.status === 'instalando' && confirmarPrevisao === 'alterar') {
          if (form.data_prevista) fd.append('data_prevista', form.data_prevista);
        } else if (form.status === 'instalacao_concluida') {
          if (form.data_conclusao) fd.append('data_conclusao', form.data_conclusao);
          if (fotoFile) fd.append('foto', fotoFile);
        }
        await api.patch(`/instalador/${editing.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else if (isPosVenda()) {
        await api.patch(`/pos-venda/${editing.id}/`, {
          status: form.status,
          data_entrega: form.data_entrega || null,
          observacoes: form.observacoes || '',
        });
        // Atualiza instalador sempre que o Pós Venda informar instalador/data prevista — em qualquer status
        // (o backend avança o status do instalador para "instalacao_programada" automaticamente)
        const deveAtualizarInstalador = editingInstalador && (form.instalador_designado || form.data_prevista_instalacao);
        if (deveAtualizarInstalador) {
          const instaladorPatch = {};
          if (form.instalador_designado) instaladorPatch.instalador = form.instalador_designado;
          if (form.data_prevista_instalacao) instaladorPatch.data_prevista = form.data_prevista_instalacao;
          if (Object.keys(instaladorPatch).length > 0) {
            await api.patch(`/instalador/${editingInstalador.id}/`, instaladorPatch);
          }
        }
      } else if (isEngenheiro()) {
        // Status é avançado via botões dedicados (avancar-status endpoint)
        await api.patch(`/engenheiro/${editing.id}/`, {
          observacoes: form.observacoes || '',
        });
      } else if (isChefe() || isGerenteComercial()) {
        // ── CHEFE/GERENTE: alteração direta de status ──
        const fd = new FormData();
        const novoSt = form.novoStatus || editing.status;
        if (novoSt !== editing.status) fd.append('status', novoSt);
        if (novoSt === 'visita' && form.data_visita) fd.append('data_visita', form.data_visita);
        if (novoSt === 'em_espera') {
          if (form.motivo_espera) fd.append('motivo_espera', form.motivo_espera);
          if (form.prazo_resposta_vendedor) fd.append('prazo_resposta_vendedor', form.prazo_resposta_vendedor);
        }
        if (novoSt === 'perdido' && form.motivo_desistencia) fd.append('motivo_desistencia', form.motivo_desistencia);
        await api.patch(`/leads/${editing.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        // ── VENDEDOR: lógica baseada no status atual ──
        const fd = new FormData();
        const st = editing.status;

        if (st === 'aguardando_vendedor') {
          if (form.entrou_em_contato !== 'sim') {
            // Não entrou em contato — não muda nada
            handleClose(); setSaving(false); return;
          }
          fd.append('entrado_em_contato', 'true');
          fd.append('status', form.novoStatus || 'aguardando_vendedor');
          if (form.novoStatus === 'visita') {
            if (form.data_visita) fd.append('data_visita', form.data_visita);
          } else if (form.novoStatus === 'em_espera') {
            if (form.motivo_espera) fd.append('motivo_espera', form.motivo_espera);
            if (form.prazo_resposta_vendedor) fd.append('prazo_resposta_vendedor', form.prazo_resposta_vendedor);
          } else if (form.novoStatus === 'perdido') {
            if (form.motivo_desistencia) fd.append('motivo_desistencia', form.motivo_desistencia);
          }

        } else if (st === 'perdido') {
          fd.append('status', form.novoStatus || st);
          if (form.novoStatus === 'visita') {
            if (form.data_visita) fd.append('data_visita', form.data_visita);
          } else if (form.novoStatus === 'em_espera') {
            if (form.motivo_espera) fd.append('motivo_espera', form.motivo_espera);
            if (form.prazo_resposta_vendedor) fd.append('prazo_resposta_vendedor', form.prazo_resposta_vendedor);
          } else if (form.novoStatus === 'perdido') {
            if (form.motivo_desistencia) fd.append('motivo_desistencia', form.motivo_desistencia);
          }

        } else if (st === 'visita' || st === 'em_espera') {
          if (form.proposta_apresentada !== 'sim') {
            // Não apresentou: só atualiza data_visita
            if (form.data_visita) fd.append('data_visita', form.data_visita);
          } else {
            fd.append('proposta_apresentada', 'true');
            if (form.valor_total_venda) fd.append('valor_total_venda', form.valor_total_venda);
            if (form.forma_pagamento) fd.append('forma_pagamento', form.forma_pagamento);
            if (form.como_foi_venda) fd.append('como_foi_venda', form.como_foi_venda);

            if (aceitouProposta === 'nao') {
              if (!naoAceitouOpcao) { alert('Por favor, selecione uma opção.'); setSaving(false); return; }
              if (naoAceitouOpcao === 'desistiu') {
                fd.append('status', 'perdido');
                if (form.motivo_desistencia) fd.append('motivo_desistencia', form.motivo_desistencia);
              } else {
                // tentar_novamente: status continua visita, atualiza data_visita
                fd.append('status', 'visita');
                if (form.data_visita) fd.append('data_visita', form.data_visita);
              }
            } else if (aceitouProposta === 'sim') {
              if (confirmarVendaFechada !== 'sim') { setSaving(false); return; }
              // Garantir que um kit foi escolhido entre os candidatos da proposta (ou na tabela completa, se não houver candidatos)
              if (!kitVendaId) {
                alert('Selecione o kit solar antes de fechar a venda.');
                setSaving(false); return;
              }
              const candidatoEscolhido = (editing.kits_propostos || []).find(c => String(c.kit_id) === String(kitVendaId));
              const valorParaFechar = candidatoEscolhido?.valor_proposto ?? (kitVendaKitObj?.valor ?? null);
              fd.append('status', 'fechado');
              fd.append('kit', kitVendaId);
              if (valorParaFechar != null) fd.append('valor_total_venda', valorParaFechar);
              if (provaFile) fd.append('prova_venda', provaFile);
            } else {
              setSaving(false); return;
            }
          }
        }

        await api.patch(`/leads/${editing.id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      handleClose();
      setToast('Dados atualizados com sucesso!');
      setTimeout(() => setToast(''), 3000);
      fetch();
    } catch (e) {
      alert('Erro: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSaving(false); }
  };

  // Inline vendedor reassign (chefe only)
  const startEditVendedor = (lead) => {
    setEditingVendedorId(lead.id);
    setVendedorDraft(String(lead.vendedor || ''));
  };
  const cancelEditVendedor = () => { setEditingVendedorId(null); setVendedorDraft(''); };
  const saveVendedor = async (leadId) => {
    if (!vendedorDraft) return;
    setSavingVendedor(true);
    try {
      const r = await api.patch(`/leads/${leadId}/reassign-vendedor/`, { vendedor_id: parseInt(vendedorDraft) });
      setLeads(prev => prev.map(l => l.id === leadId
        ? { ...l, vendedor: parseInt(vendedorDraft), vendedor_nome: r.data.vendedor_nome }
        : l
      ));
      setEditingVendedorId(null);
    } catch (e) {
      alert('Erro ao alterar vendedor: ' + (e.response?.data?.error || e.message));
    } finally { setSavingVendedor(false); }
  };

  const enviarPreVenda = async () => {
    if (!preVendaNota.trim()) { alert('Informe o motivo.'); return; }
    setSavingPreVenda(true);
    try {
      await api.post(`/leads/${preVendaModal.id}/enviar-pre-venda/`, {
        nota: preVendaNota,
        responsavel_id: preVendaResponsavelId || null,
      });
      setPreVendaModal(null);
      setPreVendaNota('');
      setPreVendaResponsavelId('');
      fetch();
      setToast('Lead enviado para Pré Venda.');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSavingPreVenda(false); }
  };

  // ── Kit inline edit ──────────────────────────────────────────────────
  const startEditKit = (lead) => {
    setEditingKitId(lead.id);
    setKitDraft(String(lead.kit || ''));
  };
  const cancelEditKit = () => { setEditingKitId(null); setKitDraft(''); };
  const saveKit = async (leadId) => {
    if (!kitDraft) return;
    setSavingKit(true);
    try {
      await api.patch(`/leads/${leadId}/`, { kit: parseInt(kitDraft) });
      const kit = kitsLista.find(k => k.id === parseInt(kitDraft));
      setLeads(prev => prev.map(l => l.id === leadId
        ? { ...l, kit: parseInt(kitDraft), kit_equipamento: kit?.equipamento, kit_marca: kit?.marca, kit_geracao: kit?.geracao }
        : l
      ));
      setEditingKitId(null);
    } catch (e) {
      alert('Erro ao alterar kit: ' + (e.response?.data?.error || e.message));
    } finally { setSavingKit(false); }
  };

  const isFechado = editing?.status === 'fechado' || editing?.lead_status === 'fechado';
  const statusOptions = isInstalador() ? INSTALADOR_STATUS : isPosVenda() ? POS_VENDA_STATUS : isEngenheiro() ? ENG_STATUS : STATUS_OPTIONS;

  // ── Definir status do engenheiro livremente (sem validação de fluxo) ────────
  const definirStatusEngenheiro = async (novoStatus) => {
    if (!editing?.id || !novoStatus || novoStatus === editing.status) return;
    setSavingEngStatus(true);
    setEngStatusError('');
    try {
      const r = await api.patch(`/engenheiro/${editing.id}/`, { status: novoStatus });
      setEditing(prev => ({ ...prev, ...r.data }));
      setLeads(prev => prev.map(l => l.id === editing.id ? { ...l, status: novoStatus } : l));
      fetch();
    } catch (e) {
      setEngStatusError(e.response?.data?.error || 'Erro ao alterar status.');
    } finally {
      setSavingEngStatus(false);
    }
  };

  // ── Avançar status do engenheiro via endpoint dedicado ──────────────────────
  const avancarEngStatus = async (novoStatus, extra = {}) => {
    setSavingEngStatus(true);
    setEngStatusError('');
    try {
      const r = await api.post(`/engenheiro/${editing.id}/avancar-status/`, { status: novoStatus, ...extra });
      // Atualiza o editing com os novos dados
      setEditing(prev => ({ ...prev, ...r.data }));
      setLeads(prev => prev.map(l => l.id === editing.id ? { ...l, status: novoStatus } : l));
      fetch();
    } catch (e) {
      setEngStatusError(e.response?.data?.error || 'Erro ao avançar status.');
    } finally {
      setSavingEngStatus(false);
    }
  };

  const confirmarCrt = async (engId, dataRetorno) => {
    setSavingEngStatus(true);
    setEngStatusError('');
    try {
      const r = await api.post(`/engenheiro/${engId}/confirmar-crt/`, { data_analise_cemig_retorno: dataRetorno });
      setEditing(prev => ({ ...prev, ...r.data }));
      fetch();
    } catch (e) {
      setEngStatusError(e.response?.data?.error || 'Erro ao confirmar CRT.');
    } finally {
      setSavingEngStatus(false);
    }
  };

  const cancelarCrt = async (engId) => {
    setSavingEngStatus(true);
    setEngStatusError('');
    try {
      const r = await api.post(`/engenheiro/${engId}/cancelar-crt/`, {});
      setEditing(prev => ({ ...prev, ...r.data }));
      fetch();
    } catch (e) {
      setEngStatusError(e.response?.data?.error || 'Erro ao cancelar CRT.');
    } finally {
      setSavingEngStatus(false);
    }
  };

  return (
    <>
    <div>
      <div style={S.heading}>Clientes</div>
      <div style={S.sub}>{isInstalador() ? 'Suas instalações designadas' : isPosVenda() ? 'Vendas fechadas — Pós Venda' : isEngenheiro() ? 'Trabalhos de engenharia' : 'Seus leads e clientes'}</div>

      {toast && (
        <div style={{ position:'fixed', top:20, right:20, background:'rgba(74,222,128,0.15)', border:'1px solid rgba(74,222,128,0.4)', borderRadius:10, padding:'12px 20px', color:'#4ade80', fontSize:13, fontWeight:600, zIndex:2000, display:'flex', alignItems:'center', gap:8 }}>
          <CheckCircle size={16} /> {toast}
        </div>
      )}

      {loadErro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{loadErro}</div>
      )}

      <div style={S.toolbar}>
        <div style={S.searchBox}>
          <Search size={14} color="#475569" />
          <input style={S.searchInput} placeholder="Buscar cliente..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select style={S.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Todos os status</option>
          {statusOptions.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        {(isChefe() || isGerenteComercial()) && (
          <div style={{ display:'flex', background:'#1a2035', borderRadius:8, overflow:'hidden', border:'1px solid #2a3550' }}>
            <button
              onClick={() => setMeusClientes(false)}
              style={{ padding:'6px 14px', fontSize:12, fontWeight:600, border:'none', cursor:'pointer',
                background: !meusClientes ? '#3b82f6' : 'transparent',
                color: !meusClientes ? '#fff' : '#94a3b8' }}
            >Todos</button>
            <button
              onClick={() => setMeusClientes(true)}
              style={{ padding:'6px 14px', fontSize:12, fontWeight:600, border:'none', cursor:'pointer',
                background: meusClientes ? '#3b82f6' : 'transparent',
                color: meusClientes ? '#fff' : '#94a3b8' }}
            >Meus Clientes</button>
          </div>
        )}
      </div>

      <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflowX:'auto', overflowY:'auto', maxHeight: isMobile ? '330px' : '490px' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth: isMobile ? 600 : 'unset' }}>
          <thead>
            <tr>
              <th style={S.th}>Cliente</th>
              <th style={S.th}>Cidade</th>
              <th style={S.th}>Telefone</th>
              {(isChefe() || isGerenteComercial()) ? (
                <>
                  <th style={S.th}>Vendedor</th>
                  <th style={S.th}>Status Vendedor</th>
                  <th style={S.th}>Status Pós Venda</th>
                  <th style={S.th}>Status Engenheiro</th>
                  <th style={S.th}>Status Instalador</th>
                  <th style={S.th}>Status Financeiro</th>
                </>
              ) : (
                <>
                  {isPosVenda() && <>
                    <th style={S.th}>Vendedor</th>
                    <th style={S.th}>Instalador</th>
                    <th style={S.th}>Início Instalação</th>
                  </>}
                  {isEngenheiro() && <th style={S.th}>Kit</th>}
                  <th style={S.th}>Status</th>
                </>
              )}
              {(isChefe() || isVendedor() || isGerenteComercial()) && <th style={S.th}>Kit</th>}
              {(isChefe() || isVendedor() || isGerenteComercial()) && <th style={S.th}>Valor da Venda</th>}
              {(isChefe() || isVendedor() || isGerenteComercial()) && <th style={S.th}>Comissão</th>}
              <th style={S.th}>Modificado</th>
              <th style={S.th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => {
              const st = l.status;
              const ctx = isPosVenda() ? 'pos_venda' : isEngenheiro() ? 'engenheiro' : isInstalador() ? 'instalador' : 'lead';
              const color = STATUS_COLOR_EXT[st] || '#64748b';
              // Chefe/Gerente nunca tem bloqueio; outros perfis têm bloqueio no status final
              const closed = (isChefe() || isGerenteComercial())
                ? false
                : isInstalador()
                  ? st === 'instalacao_concluida'
                  : isPosVenda()
                    ? st === 'finalizado'
                    : isEngenheiro()
                      ? st === 'vistoria_feita'
                      : st === 'fechado';

              const tagFor = (val, labelMap) => {
                if (!val) return <span style={{ color:'#1e2536', fontSize:11 }}>—</span>;
                const c = STATUS_COLOR_EXT[val] || '#64748b';
                return <span style={S.tag(c)}>{labelMap[val] || val}</span>;
              };

              return (
                <tr key={l.id}
                  onMouseEnter={e => e.currentTarget.style.background='#111827'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <td style={S.td}><button style={{ background:'none', border:'none', color:'#e2e8f0', fontWeight:700, cursor:'pointer', padding:0, fontSize:'inherit', textDecoration:'underline', textDecorationColor:'rgba(226,232,240,0.3)', textUnderlineOffset:2 }} onClick={() => navigate(`/jornada/${isInstalador() ? l.cliente : (l.cliente || l.cliente_id)}`)}>{l.cliente_nome}</button></td>
                  <td style={S.td}>{l.cliente_cidade || l.cidade}</td>
                  <td style={S.td}>{l.cliente_telefone || '—'}</td>
                  {(isChefe() || isGerenteComercial()) ? (
                    <>
                      {/* Vendedor — clicável para trocar (bloqueado se venda fechada) */}
                      <td style={S.td}>
                        {editingVendedorId === l.id ? (
                          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                            <select
                              value={vendedorDraft}
                              onChange={e => setVendedorDraft(e.target.value)}
                              style={{ padding:'4px 8px', borderRadius:6, background:'#0f1117', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:12, outline:'none' }}
                            >
                              <option value="">Selecione...</option>
                              {vendedores.map(v => <option key={v.id} value={v.id}>{v.nome || v.user?.first_name}</option>)}
                            </select>
                            <button
                              style={{ background:'rgba(74,222,128,0.15)', border:'1px solid rgba(74,222,128,0.35)', color:'#4ade80', borderRadius:5, padding:'3px 8px', cursor: savingVendedor ? 'not-allowed' : 'pointer', fontSize:12, fontWeight:700 }}
                              onClick={() => saveVendedor(l.id)}
                              disabled={savingVendedor}
                            >✓</button>
                            <button
                              style={{ background:'rgba(248,113,113,0.15)', border:'1px solid rgba(248,113,113,0.35)', color:'#f87171', borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:12, fontWeight:700 }}
                              onClick={cancelEditVendedor}
                            >✕</button>
                            {l.status !== 'fechado' && (
                              <button
                                style={{ background:'rgba(251,146,60,0.15)', border:'1px solid rgba(251,146,60,0.35)', color:'#fb923c', borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}
                                onClick={() => { cancelEditVendedor(); setPreVendaModal(l); setPreVendaNota(''); }}
                              >→ PV</button>
                            )}
                          </div>
                        ) : l.status === 'fechado' ? (
                          /* Venda fechada — não pode trocar vendedor */
                          <span style={{ fontSize:13, color:'#64748b' }}>{l.vendedor_nome || '—'}</span>
                        ) : (
                          /* Clicável — abre inline edit */
                          <button
                            onClick={() => startEditVendedor(l)}
                            style={{ background:'none', border:'none', color:'#60a5fa', fontSize:13, cursor:'pointer', padding:0, textDecoration:'underline', textDecorationColor:'rgba(96,165,250,0.35)', textUnderlineOffset:2 }}
                          >{l.vendedor_nome || '—'}</button>
                        )}
                      </td>
                      <td style={S.td}>{tagFor(l.status, STATUS_LABEL_LEAD)}</td>
                      <td style={S.td}>{tagFor(l.pos_venda_status, STATUS_LABEL_POS_VENDA)}</td>
                      <td style={S.td}>{tagFor(l.engenheiro_status, STATUS_LABEL_ENG)}</td>
                      <td style={S.td}>{tagFor(l.instalador_status, STATUS_LABEL_INSTALADOR)}</td>
                      <td style={S.td}>{tagFor(l.financeiro_status, STATUS_LABEL_FIN)}</td>
                    </>
                  ) : (
                    <>
                      {isPosVenda() && <>
                        <td style={S.td}><span style={{ fontSize:13, color:'#cbd5e1' }}>{l.vendedor_nome || '—'}</span></td>
                        <td style={S.td}><span style={{ fontSize:13, color: l.instalador_nome ? '#cbd5e1' : '#475569' }}>{l.instalador_nome || '—'}</span></td>
                        <td style={S.td}><span style={{ fontSize:12, color: l.data_inicio_instalacao ? '#60a5fa' : '#475569' }}>{l.data_inicio_instalacao ? new Date(l.data_inicio_instalacao).toLocaleString('pt-BR', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</span></td>
                      </>}
                      {isEngenheiro() && (
                        <td style={S.td}>
                          {l.kit_geracao ? (
                            <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ background:'rgba(246,201,14,0.12)', color:'#f6c90e', border:'1px solid rgba(246,201,14,0.2)', borderRadius:20, padding:'2px 8px', fontSize:11, fontWeight:700 }}>
                                {l.kit_geracao} kWh
                              </span>
                              <span style={{ fontSize:11, color:'#64748b' }}>{l.kit_equipamento}</span>
                            </span>
                          ) : (
                            <span style={{ color:'#475569', fontSize:12 }}>—</span>
                          )}
                        </td>
                      )}
                      <td style={S.td}><span style={S.tag(color)}>{labelStatus(st, ctx)}</span></td>
                    </>
                  )}
                  {(isChefe() || isVendedor() || isGerenteComercial()) && (
                    <td style={S.td}>
                      {l.status === 'fechado' ? (
                        /* Venda fechada — read-only */
                        l.kit_equipamento
                          ? <span style={{ fontSize:12, color:'#94a3b8' }}>{l.kit_equipamento}{l.kit_geracao ? ` · ${l.kit_geracao}kWh` : ''}</span>
                          : <span style={{ color:'#475569', fontSize:12 }}>—</span>
                      ) : (
                        /* Clicável — abre modal com tabela de kits */
                        <button
                          onClick={() => setKitTableModal({
                            leadId: l.id,
                            clienteNome: l.cliente_nome,
                            callback: async (kitId, kit) => {
                              setKitTableModal(null);
                              setSavingKit(true);
                              try {
                                await api.patch(`/leads/${l.id}/`, { kit: kitId });
                                setLeads(prev => prev.map(x => x.id === l.id
                                  ? { ...x, kit: kitId, kit_equipamento: kit.equipamento, kit_marca: kit.marca, kit_geracao: kit.geracao }
                                  : x
                                ));
                              } catch(e) { alert('Erro ao salvar kit: ' + (e.response?.data?.error || e.message)); }
                              finally { setSavingKit(false); }
                            }
                          })}
                          style={{ background:'none', border:'none', cursor:'pointer', padding:0, textAlign:'left' }}>
                          {l.kit_equipamento
                            ? <span style={{ color:'#a78bfa', fontSize:12, textDecoration:'underline', textDecorationColor:'rgba(167,139,250,0.35)', textUnderlineOffset:2 }}>
                                {l.kit_equipamento}{l.kit_geracao ? ` · ${l.kit_geracao}kWh` : ''}
                              </span>
                            : <span style={{ color:'#475569', fontSize:12, textDecoration:'underline', textDecorationColor:'rgba(71,85,105,0.3)', textUnderlineOffset:2 }}>+ Escolher kit</span>
                          }
                        </button>
                      )}
                    </td>
                  )}
                  {(isChefe() || isVendedor() || isGerenteComercial()) && (
                    <td style={S.td}>
                      {l.valor_total_venda != null
                        ? <span style={{ color:'#f6c90e', fontWeight:600 }}>R$ {Number(l.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
                        : <span style={{ color:'#475569' }}>—</span>}
                    </td>
                  )}
                  {(isChefe() || isVendedor() || isGerenteComercial()) && (
                    <td style={S.td}>
                      {l.comissao_valor != null
                        ? <span style={{ color:'#4ade80', fontWeight:700 }}>
                            R$ {Number(l.comissao_valor).toLocaleString('pt-BR', { minimumFractionDigits:2 })}
                            <span style={{ color:'#475569', fontWeight:400, fontSize:11, marginLeft:4 }}>
                              ({l.percentual_comissao}%)
                            </span>
                          </span>
                        : <span style={{ color:'#475569', fontSize:12 }}>—</span>}
                    </td>
                  )}
                  <td style={S.td}>{l.data_modificacao ? new Date(l.data_modificacao).toLocaleDateString('pt-BR') : '—'}</td>
                  <td style={S.td}>
                    <div style={{ display:'flex', gap:6, flexWrap:'nowrap' }}>
                      {!closed && <button style={S.btn('#60a5fa')} onClick={() => openEdit(l)}>Editar</button>}
                      {isChefe() && st === 'fechado' && <button style={S.btn('#a78bfa')} onClick={() => setVoltarFunilLead(l)}>↩ Funil</button>}
                      {closed && !isChefe() && !isGerenteComercial() && (
                        isVendedor()
                          ? <button style={S.btn('#475569')} onClick={() => openEdit(l)}>👁 Ver</button>
                          : <span style={{ fontSize:11, color:'#475569' }}>Fechado</span>
                      )}

                      {isChefe() && <button style={S.btn('#f87171')} onClick={() => handleDelete(l)}>Deletar</button>}
                      {(l.cliente_cidade || l.cidade) && (
                        <button style={S.btn('#4ade80')} onClick={() => {
                          const addr = [l.cliente_endereco, l.cliente_cidade || l.cidade, l.cliente_estado].filter(Boolean).join(', ');
                          window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`, '_blank');
                        }}>Ver Mapa</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={(isChefe() || isGerenteComercial()) ? 14 : isVendedor() ? 9 : isPosVenda() ? 9 : isEngenheiro() ? 7 : 6} style={{ ...S.td, textAlign:'center', color:'#475569', padding:32 }}>Nenhum cliente encontrado</td></tr>}
          </tbody>
        </table>
      </div>

      {fotosInstalModal && <FotosInstalModal fotos={fotosInstalModal} onClose={() => setFotosInstalModal(null)} />}
      {editing && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
          <div style={S.modal}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={S.modalTitle}>Editar Status da Venda</div>
              <button style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b' }} onClick={handleClose}><X size={20} /></button>
            </div>

            {/* Dados do cliente */}
            <div style={S.sec}>Dados do Cliente</div>
            {(isVendedor() || isChefe() || isGerenteComercial() || isPreVenda()) && editing.status !== 'fechado' && (
              <div style={{ marginBottom:16 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 12px' }}>
                  <div>
                    <label style={S.label}>Nome</label>
                    <input style={S.input} value={clienteForm.nome || ''} onChange={e => setClienteForm(p=>({...p,nome:e.target.value}))} />
                  </div>
                  <div>
                    <label style={S.label}>Telefone</label>
                    <input style={S.input} value={clienteForm.telefone || ''} onChange={e => setClienteForm(p=>({...p,telefone:e.target.value}))} />
                  </div>
                  <div>
                    <label style={S.label}>E-mail</label>
                    <input style={S.input} value={clienteForm.email || ''} onChange={e => setClienteForm(p=>({...p,email:e.target.value}))} />
                  </div>
                  <div>
                    <label style={S.label}>Consumo Mensal (R$)</label>
                    <input style={S.input} type="number" value={clienteForm.consumo_mensal || ''} onChange={e => setClienteForm(p=>({...p,consumo_mensal:e.target.value}))} />
                  </div>
                  <div>
                    <label style={S.label}>CPF / CNPJ</label>
                    <input style={S.input} value={clienteForm.cpf_doc || ''} onChange={e => setClienteForm(p=>({...p,cpf_doc:e.target.value}))} />
                  </div>
                  <div>
                    <label style={S.label}>RG</label>
                    <input style={S.input} value={clienteForm.rg_doc || ''} onChange={e => setClienteForm(p=>({...p,rg_doc:e.target.value}))} />
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={S.label}>Endereço</label>
                    <input style={S.input} value={clienteForm.endereco || ''} onChange={e => setClienteForm(p=>({...p,endereco:e.target.value}))} />
                  </div>
                  <div>
                    <label style={S.label}>Cidade</label>
                    <input style={S.input} value={clienteForm.cidade || ''} onChange={e => setClienteForm(p=>({...p,cidade:e.target.value}))} />
                  </div>
                  <div>
                    <label style={S.label}>Estado</label>
                    <input style={S.input} value={clienteForm.estado || ''} onChange={e => setClienteForm(p=>({...p,estado:e.target.value}))} placeholder="MG" maxLength={2} />
                  </div>
                </div>
                <button
                  style={{ ...S.btn('#4ade80'), padding:'8px 18px', fontSize:12, opacity: savingCliente ? 0.6 : 1, marginTop:4 }}
                  onClick={saveClienteData} disabled={savingCliente}>
                  {savingCliente ? 'Salvando...' : '💾 Salvar dados do cliente'}
                </button>
              </div>
            )}
            {isInstalador() && editing.diagrama_unifilar_url && (
              <div style={{ margin:'0 0 12px 0', background:'rgba(96,165,250,0.07)', border:'1px solid rgba(96,165,250,0.25)', borderRadius:9, padding:'10px 14px', display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:13, color:'#94a3b8' }}>📐 Diagrama Unifilar:</span>
                <a href={editing.diagrama_unifilar_url} target="_blank" rel="noopener noreferrer"
                  style={{ color:'#60a5fa', fontWeight:700, fontSize:13, textDecoration:'none' }}>
                  Ver diagrama →
                </a>
              </div>
            )}
            {(isInstalador() ? [
              ['Cliente',              editing.cliente_nome],
              ['Telefone',             editing.cliente_telefone],
              ['Endereço',             editing.cliente_endereco],
              ['Cidade',               editing.cliente_cidade || editing.cidade],
              ['Vendedor',             editing.vendedor_nome],
              ['Qtd. Placas',          editing.quantidade_placas ? `${editing.quantidade_placas} placas` : '—'],
              ['Início Instalação',    fmtDT(editing.data_inicio)],
              ['Previsão Conclusão',   fmtD(editing.data_prevista)],
            ] : [
              ['Cliente',      editing.cliente_nome],
              ['Telefone',     editing.cliente_telefone || '—'],
              ['E-mail',       editing.cliente_email || '—'],
              ['Endereço',     editing.cliente_endereco || '—'],
              ['Cidade',       editing.cliente_cidade || editing.cidade],
              ...((isEngenheiro() || isChefe()) ? [
                ['CPF',        editing.cliente_cpf_doc || '—'],
                ['RG',         editing.cliente_rg_doc  || '—'],
              ] : []),
              ['Vendedor',     editing.vendedor_nome],
              ['Valor da Venda', editing.valor_total_venda ? `R$ ${Number(editing.valor_total_venda).toLocaleString('pt-BR')}` : '—'],
              ...((isVendedor() || isChefe() || isGerenteComercial()) ? [
                ['Prazo Vendedor', editing.prazo_resposta_vendedor ? new Date(editing.prazo_resposta_vendedor).toLocaleString('pt-BR') : '—'],
              ] : []),
              ['Criado em',    editing.criado_em ? new Date(editing.criado_em).toLocaleString('pt-BR') : '—'],
            ]).map(([k,v]) => (
              <div key={k} style={S.infoRow}>
                <span style={S.infoLabel}>{k}</span>
                <span style={S.infoVal}>{v || '—'}</span>
              </div>
            ))}

            {/* Campos editáveis */}
            <div style={S.sec}>Editar</div>
            {/* ── MODAL CHEFE/GERENTE (gestão direta) ── */}
            {(isChefe() || isGerenteComercial()) && (() => {
              const st = editing.status;

              if (st === 'fechado') return null; // já tratado acima no case fechado

              const STATUS_OPTIONS_CHEFE = [
                { v:'aguardando_vendedor', l:'Aguardando Vendedor' },
                { v:'visita',              l:'Visita Agendada' },
                { v:'proposta',            l:'Proposta Enviada' },
                { v:'em_espera',           l:'Aguardando Cliente' },
                { v:'perdido',             l:'Desistiu da Compra' },
              ];

              return (
                <>
                  <label style={S.label}>Alterar status do lead</label>
                  <select style={S.inputSel} value={form.novoStatus || st} onChange={e => setForm(p => ({ ...p, novoStatus: e.target.value }))}>
                    {STATUS_OPTIONS_CHEFE.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>

                  {(form.novoStatus || st) === 'visita' && (
                    <div>
                      <label style={S.label}>Data da Visita</label>
                      <input style={S.input} type="datetime-local" value={form.data_visita || ''} onChange={f('data_visita')} />
                    </div>
                  )}
                  {(form.novoStatus || st) === 'em_espera' && (
                    <>
                      <label style={S.label}>O que está aguardando?</label>
                      <textarea style={{ ...S.input, height:70, resize:'vertical' }} value={form.motivo_espera || ''} onChange={f('motivo_espera')} />
                      <label style={S.label}>Data de retorno prevista</label>
                      <input style={S.input} type="datetime-local" value={form.prazo_resposta_vendedor || ''} onChange={f('prazo_resposta_vendedor')} />
                    </>
                  )}
                  {(form.novoStatus || st) === 'perdido' && (
                    <>
                      <label style={S.label}>Motivo de desistência</label>
                      <textarea style={{ ...S.input, height:70, resize:'vertical' }} value={form.motivo_desistencia || ''} onChange={f('motivo_desistencia')} />
                    </>
                  )}
                </>
              );
            })()}

            {/* ── MODAL VENDEDOR (lógica por status atual) ── */}
            {!isChefe() && !isGerenteComercial() && !isPosVenda() && !isEngenheiro() && !isInstalador() && (() => {
              const st = editing.status;

              // Bloco reutilizável: 3 opções — chamado como função (não como componente)
              // para evitar desmonte/remonte a cada keystroke
              const tresOpcoes = () => (
                <>
                  <label style={S.label}>O que aconteceu?</label>
                  <select style={S.inputSel} value={form.novoStatus || ''} onChange={e => setForm(p => ({ ...p, novoStatus: e.target.value }))}>
                    <option value="">— Selecione —</option>
                    <option value="visita">Visita Agendada</option>
                    <option value="em_espera">Aguardando Cliente</option>
                    <option value="perdido">Desistiu da Compra</option>
                  </select>

                  {form.novoStatus === 'visita' && (
                    <div>
                      <label style={S.label}>Data da Visita</label>
                      <input style={S.input} type="datetime-local" value={form.data_visita || ''} onChange={f('data_visita')} />
                    </div>
                  )}
                  {form.novoStatus === 'em_espera' && (
                    <>
                      <label style={S.label}>O que está aguardando?</label>
                      <textarea style={{ ...S.input, height:70, resize:'vertical' }} value={form.motivo_espera || ''} onChange={f('motivo_espera')} />
                      <label style={S.label}>Data para eu retornar</label>
                      <input style={S.input} type="datetime-local" value={form.prazo_resposta_vendedor || ''} onChange={f('prazo_resposta_vendedor')} />
                    </>
                  )}
                  {form.novoStatus === 'perdido' && (
                    <>
                      <label style={S.label}>Motivo de desistência</label>
                      <textarea style={{ ...S.input, height:70, resize:'vertical' }} value={form.motivo_desistencia || ''} onChange={f('motivo_desistencia')} />
                    </>
                  )}
                </>
              );

              /* ── CASE 1: Aguardando Vendedor / Pré Venda ── */
              if (st === 'aguardando_vendedor' || st === 'pre_venda') return (
                <>
                  {editing.prazo_resposta_vendedor && (
                    <div style={{ background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.3)', borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
                      <div style={{ fontSize:11, color:'#60a5fa', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:4 }}>Prazo para entrar em contato</div>
                      <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>
                        {new Date(editing.prazo_resposta_vendedor).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  )}
                  <label style={S.label}>Entrou em contato?</label>
                  <select style={S.inputSel} value={form.entrou_em_contato || ''} onChange={f('entrou_em_contato')}>
                    <option value="">— Selecione —</option>
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                  </select>
                  {form.entrou_em_contato === 'sim' && tresOpcoes()}
                </>
              );

              /* ── CASE 2: Desistiu da Compra ── */
              if (st === 'perdido') return tresOpcoes();

              /* ── CASE 3: Aguardando Cliente ── (usa mesmo fluxo de visita/proposta) */

              /* ── CASE 4: Visita Agendada / Proposta Enviada / Aguardando Cliente ── */
              if (st === 'visita' || st === 'proposta' || st === 'em_espera') return (
                <>
                  {editing.data_visita && (
                    <div style={{ background:'rgba(251,146,60,0.08)', border:'1px solid rgba(251,146,60,0.3)', borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
                      <div style={{ fontSize:11, color:'#fb923c', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:4 }}>Visita agendada para</div>
                      <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>
                        {new Date(editing.data_visita).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  )}
                  <label style={S.label}>Apresentou a proposta?</label>
                  <select style={S.inputSel} value={form.proposta_apresentada || ''} onChange={f('proposta_apresentada')}>
                    <option value="">— Selecione —</option>
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                  </select>

                  {form.proposta_apresentada === 'nao' && (
                    <div style={{ background:'rgba(251,146,60,0.08)', border:'1px solid rgba(251,146,60,0.3)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                      <div style={{ fontSize:13, color:'#fb923c', marginBottom:12 }}>
                        É necessário apresentar a proposta. Por favor, marque como sim ou informe uma nova data para a visita.
                      </div>
                      <label style={S.label}>Nova Data da Visita</label>
                      <input style={{ ...S.input, marginBottom:0 }} type="datetime-local" value={form.data_visita || ''} onChange={f('data_visita')} />
                    </div>
                  )}

                  {form.proposta_apresentada === 'sim' && (
                    <>
                      {/* Valor e forma de pagamento vêm da geração de proposta — somente leitura */}
                      <div style={{ background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.18)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                        <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:8 }}>Dados da Proposta Gerada</div>
                        <div style={S.row2}>
                          <div>
                            <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>Valor Total</div>
                            <div style={{ fontSize:16, fontWeight:700, color: editing.valor_total_venda ? '#f6c90e' : '#475569' }}>
                              {editing.valor_total_venda
                                ? `R$ ${Number(editing.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}`
                                : 'Gere a proposta na aba Documentos'}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>Forma de Pagamento</div>
                            <div style={{ fontSize:15, fontWeight:600, color: editing.forma_pagamento ? '#e2e8f0' : '#475569' }}>
                              {editing.forma_pagamento || '—'}
                            </div>
                            {editing.valor_total_com_taxa && (
                              <div style={{ fontSize:12, color:'#94a3b8', marginTop:4 }}>
                                Com taxa: <span style={{ color:'#f6c90e', fontWeight:700 }}>R$ {Number(editing.valor_total_com_taxa).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <label style={S.label}>Como foi a conversa</label>
                      <textarea style={{ ...S.input, height:75, resize:'vertical' }} value={form.como_foi_venda || ''} onChange={f('como_foi_venda')} />

                      <label style={S.label}>Cliente aceitou a proposta?</label>
                      <select style={S.inputSel} value={aceitouProposta} onChange={e => { setAceitouProposta(e.target.value); setNaoAceitouOpcao(''); setConfirmarVendaFechada(''); }}>
                        <option value="">— Selecione —</option>
                        <option value="nao">Não</option>
                        <option value="sim">Sim</option>
                      </select>

                      {aceitouProposta === 'nao' && (
                        <>
                          <div style={{ fontSize:13, color:'#94a3b8', marginBottom:12 }}>
                            Ok, deseja marcar que desistiu da compra ou tentará em outro momento?
                          </div>
                          <select style={S.inputSel} value={naoAceitouOpcao} onChange={e => setNaoAceitouOpcao(e.target.value)}>
                            <option value="">— Selecione —</option>
                            <option value="desistiu">Desistiu da Compra</option>
                            <option value="tentar_novamente">Tentará em Outro Momento</option>
                          </select>
                          {naoAceitouOpcao === 'desistiu' && (
                            <>
                              <label style={S.label}>Motivo de desistência</label>
                              <textarea style={{ ...S.input, height:70, resize:'vertical' }} value={form.motivo_desistencia || ''} onChange={f('motivo_desistencia')} />
                            </>
                          )}
                          {naoAceitouOpcao === 'tentar_novamente' && (
                            <>
                              <label style={S.label}>Data de Nova Tentativa</label>
                              <input style={S.input} type="datetime-local" value={form.data_visita || ''} onChange={f('data_visita')} />
                            </>
                          )}
                        </>
                      )}

                      {aceitouProposta === 'sim' && (
                        <>
                          {/* ── Confirme o Kit — escolha entre os candidatos da proposta ── */}
                          <div style={{ background:'rgba(167,139,250,0.07)', border:'1px solid rgba(167,139,250,0.25)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:'#a78bfa', marginBottom:10 }}>
                              📦 Confirme o Kit
                            </div>
                            {(editing.kits_propostos && editing.kits_propostos.length > 0) ? (
                              /* Tem 1, 2 ou 3 candidatos da proposta — vendedor/chefe escolhe qual foi vendido */
                              <>
                                <div style={{ fontSize:13, color:'#94a3b8', marginBottom:10 }}>
                                  Qual opção de kit foi vendida ao cliente?
                                </div>
                                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                  {editing.kits_propostos.map((c, idx) => {
                                    const isSel = String(kitVendaId) === String(c.kit_id);
                                    return (
                                      <label key={c.kit_id || idx}
                                        onClick={() => { setKitVendaId(String(c.kit_id)); setKitVendaConfirmado(true); setKitVendaKitObj(null); }}
                                        style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer', padding:'10px 12px',
                                                 borderRadius:8, border:'1px solid ' + (isSel ? 'rgba(167,139,250,0.6)' : '#1a2035'),
                                                 background: isSel ? 'rgba(167,139,250,0.12)' : 'transparent' }}>
                                        <input type="radio" checked={isSel} onChange={() => {}}
                                          style={{ accentColor:'#a78bfa', width:15, height:15, marginTop:2 }} />
                                        <div>
                                          <div style={{ fontSize:13, fontWeight:700, color:'#e2e8f0' }}>
                                            {editing.kits_propostos.length > 1 ? `Opção ${idx + 1} · ` : ''}
                                            {c.kit_marca ? `${c.kit_marca} ` : ''}{c.kit_equipamento || '—'}
                                          </div>
                                          <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>
                                            {c.kit_geracao ? `${c.kit_geracao} kWh/mês · ` : ''}
                                            {c.kit_qtd_inversores != null ? `${c.kit_qtd_inversores} inversor(es) · ` : ''}
                                            <span style={{ color:'#f6c90e', fontWeight:600 }}>
                                              {c.valor_proposto != null ? `R$ ${Number(c.valor_proposto).toLocaleString('pt-BR', { minimumFractionDigits:2 })}` : '—'}
                                            </span>
                                          </div>
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              </>
                            ) : (
                              /* Zero candidatos (lead sem proposta gerada) — bloqueia fechamento até escolher na tabela completa */
                              <>
                                <div style={{ fontSize:13, color:'#fb923c', marginBottom:8 }}>
                                  ⚠️ Nenhum kit foi proposto para esta venda. Selecione o kit na tabela completa antes de fechar:
                                </div>
                                {kitVendaId ? (
                                  <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
                                    <span style={{ fontSize:13, color:'#a78bfa', fontWeight:600 }}>
                                      ✓ {kitVendaKitObj?.equipamento || kitsLista.find(k=>k.id===parseInt(kitVendaId))?.equipamento}
                                    </span>
                                    <button onClick={() => { setKitVendaId(''); setKitVendaKitObj(null); }}
                                      style={{ background:'none', border:'none', color:'#f87171', fontSize:12, cursor:'pointer', padding:0 }}>✕</button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setKitTableModal({
                                      clienteNome: editing?.cliente_nome,
                                      callback: (kitId, kit) => { setKitVendaId(String(kitId)); setKitVendaKitObj(kit); setKitTableModal(null); }
                                    })}
                                    style={{ marginTop:8, display:'flex', alignItems:'center', gap:6,
                                             background:'rgba(246,201,14,0.12)', border:'1px solid rgba(246,201,14,0.35)',
                                             color:'#f6c90e', borderRadius:8, padding:'7px 14px',
                                             fontSize:13, fontWeight:700, cursor:'pointer' }}>
                                    📦 Selecionar kit na tabela
                                  </button>
                                )}
                              </>
                            )}
                          </div>

                          {/* ── Confirmação final — só aparece se kit OK ── */}
                          {!!kitVendaId && (
                          <div style={{ background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.25)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                            <div style={{ fontSize:14, fontWeight:700, color:'#4ade80', marginBottom:12 }}>
                              🎉 Perfeito! Confirmar Venda Fechada?
                            </div>
                            <select style={S.inputSel} value={confirmarVendaFechada} onChange={e => setConfirmarVendaFechada(e.target.value)}>
                              <option value="">— Selecione —</option>
                              <option value="nao">Não</option>
                              <option value="sim">Sim</option>
                            </select>
                            {confirmarVendaFechada === 'nao' && (
                              <div style={{ fontSize:13, color:'#fb923c', marginTop:8 }}>Marque Sim por favor.</div>
                            )}
                          </div>
                          )}
                          {confirmarVendaFechada === 'sim' && (() => {
                            const fs = editing.fotos_status || {};
                            const ds = editing.documentos_status || {};
                            const secoes = [
                              {
                                titulo: 'Documentos gerados',
                                itens: [
                                  { label: 'Proposta',  ok: !!editing.proposta_gerada },
                                  { label: 'Procuração', ok: !!editing.cemig_gerado },
                                  { label: 'Contrato',  ok: !!editing.contrato_gerado },
                                ],
                              },
                              {
                                titulo: 'Fotos do imóvel',
                                itens: [
                                  { label: 'Foto padrão',            ok: !!fs.foto_padrao },
                                  { label: 'Foto disjuntor',         ok: !!fs.foto_disjuntor },
                                  { label: 'Foto medidor',           ok: !!fs.foto_medidor },
                                  { label: 'Foto estrutura telhado', ok: !!fs.foto_estrutura_telhado },
                                  { label: 'Foto frente imóvel',     ok: !!fs.foto_frente_imovel },
                                  { label: 'Foto vistoria técnica',  ok: !!fs.foto_vistoria_tecnica },
                                ],
                              },
                              {
                                titulo: 'Documentos do cliente',
                                itens: [
                                  { label: 'Conta CEMIG',  ok: !!ds.conta_cemig },
                                  { label: 'CPF do cliente', ok: !!ds.cpf_doc },
                                  { label: 'RG do cliente',  ok: !!ds.rg_doc },
                                ],
                              },
                            ];
                            const todosProntos = secoes.every(s => s.itens.every(d => d.ok));
                            return (
                              <>
                                {/* Botões para gerar documentos */}
                                <div style={{ background:'rgba(246,201,14,0.05)', border:'1px solid rgba(246,201,14,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                                  <div style={{ fontSize:12, color:'#f6c90e', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📄 Gerar Documentos</div>
                                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                    <button type="button"
                                      onClick={() => setPropostaVendaModal(true)}
                                      style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'7px 14px', borderRadius:8, background: editing.proposta_gerada ? 'rgba(74,222,128,0.1)' : 'rgba(246,201,14,0.12)', border:'1px solid ' + (editing.proposta_gerada ? 'rgba(74,222,128,0.35)' : 'rgba(246,201,14,0.35)'), color: editing.proposta_gerada ? '#4ade80' : '#f6c90e', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                                      <FileDown size={13}/> {editing.proposta_gerada ? '✓ Proposta Gerada' : 'Gerar Proposta'}
                                    </button>
                                    <button type="button" disabled={gerandoCemig}
                                      onClick={async () => {
                                        setGerandoCemig(true);
                                        try {
                                          const resp = await api.post('/documentos/gerar-cemig-lead/' + editing.id + '/', {}, { responseType:'blob' });
                                          const url = window.URL.createObjectURL(new Blob([resp.data], { type:'application/pdf' }));
                                          const a = document.createElement('a'); a.href = url; a.download = 'procuracao_' + editing.id + '.pdf'; a.click();
                                          window.URL.revokeObjectURL(url);
                                          setEditing(prev => ({ ...prev, cemig_gerado: true }));
                                        } catch(e) { alert('Erro ao gerar Procuração: ' + (e.response?.data?.error || e.message || 'tente novamente')); }
                                        finally { setGerandoCemig(false); }
                                      }}
                                      style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'7px 14px', borderRadius:8, background: editing.cemig_gerado ? 'rgba(74,222,128,0.1)' : 'rgba(96,165,250,0.1)', border:'1px solid ' + (editing.cemig_gerado ? 'rgba(74,222,128,0.35)' : 'rgba(96,165,250,0.35)'), color: editing.cemig_gerado ? '#4ade80' : '#60a5fa', fontSize:12, fontWeight:700, cursor: gerandoCemig ? 'wait' : 'pointer', opacity: gerandoCemig ? 0.6 : 1 }}>
                                      <FileDown size={13}/> {gerandoCemig ? 'Gerando...' : editing.cemig_gerado ? '✓ Procuração Gerada' : 'Gerar Procuração'}
                                    </button>
                                    <button type="button" disabled={gerandoContrato || !kitVendaId}
                                      onClick={async () => {
                                        if (!kitVendaId) { alert('Selecione o kit em "Confirme o Kit" antes de gerar o contrato.'); return; }
                                        setGerandoContrato(true);
                                        try {
                                          const resp = await api.post('/documentos/gerar-contrato/' + editing.id + '/', { kit_id: kitVendaId }, { responseType:'blob' });
                                          const url = window.URL.createObjectURL(new Blob([resp.data], { type:'application/pdf' }));
                                          const a = document.createElement('a'); a.href = url; a.download = 'contrato_' + editing.id + '.pdf'; a.click();
                                          window.URL.revokeObjectURL(url);
                                          setEditing(prev => ({ ...prev, contrato_gerado: true }));
                                        } catch(e) { alert('Erro ao gerar Contrato: ' + (e.response?.data?.error || e.message || 'tente novamente')); }
                                        finally { setGerandoContrato(false); }
                                      }}
                                      style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'7px 14px', borderRadius:8, background: editing.contrato_gerado ? 'rgba(74,222,128,0.1)' : 'rgba(167,139,250,0.1)', border:'1px solid ' + (editing.contrato_gerado ? 'rgba(74,222,128,0.35)' : 'rgba(167,139,250,0.35)'), color: editing.contrato_gerado ? '#4ade80' : '#a78bfa', fontSize:12, fontWeight:700, cursor: gerandoContrato ? 'wait' : 'pointer', opacity: gerandoContrato ? 0.6 : 1 }}>
                                      <FileDown size={13}/> {gerandoContrato ? 'Gerando...' : editing.contrato_gerado ? '✓ Contrato Gerado' : 'Gerar Contrato'}
                                    </button>
                                  </div>
                                </div>
                                {/* Checklist por seção */}
                                <div style={{ background:'rgba(15,17,23,0.8)', border:'1px solid #1a2035', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                                  <div style={{ fontSize:12, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Documentos necessários</div>
                                  {secoes.map(sec => (
                                    <div key={sec.titulo} style={{ marginBottom:10 }}>
                                      <div style={{ fontSize:10, color:'#475569', textTransform:'uppercase', letterSpacing:0.8, marginBottom:6 }}>{sec.titulo}</div>
                                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'5px 12px' }}>
                                        {sec.itens.map(d => (
                                          <div key={d.label} style={{ display:'flex', alignItems:'center', gap:8 }}>
                                            <span style={{ fontSize:14 }}>{d.ok ? '✅' : '❌'}</span>
                                            <span style={{ fontSize:12, color: d.ok ? '#4ade80' : '#f87171', fontWeight:600 }}>{d.label}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                  {!todosProntos && (
                                    <div style={{ marginTop:10, fontSize:12, color:'#fb923c', background:'rgba(251,146,60,0.08)', border:'1px solid rgba(251,146,60,0.25)', borderRadius:7, padding:'8px 12px' }}>
                                      Por favor, complete os itens faltantes antes de confirmar venda fechada.
                                    </div>
                                  )}
                                </div>
                                {/* Prova de venda só aparece se todos os documentos foram gerados */}
                                {todosProntos && (
                                  <div style={{ background:'rgba(74,222,128,0.06)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:14, marginBottom:12 }}>
                                    <label style={{ ...S.label, color:'#4ade80' }}>Prova de Venda (foto, vídeo ou documento) *</label>
                                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                      <label style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:7, background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', color:'#4ade80', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                                        <Upload size={14} /> Selecionar arquivo
                                        <input type="file" style={{ display:'none' }} onChange={e => setProvaFile(e.target.files[0])} accept="image/*,video/*,.pdf,.doc,.docx" />
                                      </label>
                                      {provaFile && <span style={{ fontSize:12, color:'#94a3b8' }}>{provaFile.name}</span>}
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </>
                  )}
                </>
              );

              /* ── CASE fechado: Venda já fechada ── */
              if (st === 'fechado') return (
                <div>
                  <div style={{ background:'rgba(74,222,128,0.06)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:14, marginBottom: isVendedor() ? 14 : 0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#4ade80', marginBottom:8 }}>✅ Venda Fechada</div>
                    <div style={{ fontSize:12, color:'#94a3b8', marginBottom:12 }}>
                      Esta venda já foi concluída. O progresso de pós-venda e engenharia pode ser acompanhado na aba Controle.
                    </div>
                    {[
                      ['Valor da Venda', editing.valor_total_venda ? `R$ ${Number(editing.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}` : '—'],
                      ['Comissão',       editing.comissao_valor != null ? `R$ ${Number(editing.comissao_valor).toLocaleString('pt-BR', { minimumFractionDigits:2 })} (${editing.percentual_comissao}%)` : null],
                      ['Vendedor',       editing.vendedor_nome || '—'],
                      ['Pós Venda',      editing.pos_venda_status || '—'],
                      ['Engenheiro',     editing.engenheiro_status || '—'],
                      ['Instalador',     editing.instalador_status || '—'],
                      ['Data Instalação', editing.instalador_data_prevista ? new Date(editing.instalador_data_prevista + 'T00:00:00').toLocaleDateString('pt-BR') : null],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                        <span style={{ fontSize:12, color:'#475569' }}>{k}</span>
                        <span style={{ fontSize:12, color:'#cbd5e1', fontWeight:600 }}>{v}</span>
                      </div>
                    ))}
                    {(editing.kit_equipamento || editing.kit_marca) && (
                      <div style={{ background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.2)', borderRadius:8, padding:'10px 14px', marginTop:12 }}>
                        <div style={{ fontSize:10, color:'#f6c90e', textTransform:'uppercase', letterSpacing:0.8, fontWeight:700, marginBottom:6 }}>📦 Kit Solar</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:10, fontSize:13, color:'#e2e8f0' }}>
                          {editing.kit_geracao != null && <span style={{ background:'rgba(246,201,14,0.12)', color:'#f6c90e', border:'1px solid rgba(246,201,14,0.2)', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>{editing.kit_geracao} kWh</span>}
                          {editing.kit_marca && <span style={{ background:'rgba(167,139,250,0.12)', color:'#a78bfa', border:'1px solid rgba(167,139,250,0.25)', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>{editing.kit_marca}</span>}
                          {editing.kit_equipamento && <span style={{ color:'#cbd5e1' }}>{editing.kit_equipamento}</span>}
                          {editing.kit_quantidade != null && <span style={{ color:'#64748b', fontSize:12 }}>{editing.kit_quantidade}x {editing.kit_placas_w || '—'}Wp</span>}
                          {editing.kit_valor != null && <span style={{ color:'#4ade80', fontWeight:700, fontSize:12 }}>R$ {Number(editing.kit_valor).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Dados do cliente (somente visualização para vendedor) ── */}
                  {isVendedor() && (
                    <div style={{ background:'rgba(30,42,64,0.6)', border:'1px solid #1a2035', borderRadius:10, padding:14, marginBottom:14 }}>
                      <div style={{ fontSize:11, color:'#60a5fa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>👤 Dados do Cliente</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 20px' }}>
                        {[
                          ['Nome',         editing.cliente_nome],
                          ['Telefone',     editing.cliente_telefone],
                          ['E-mail',       editing.cliente_email],
                          ['CPF / CNPJ',   editing.cliente_cpf_doc],
                          ['RG',           editing.cliente_rg_doc],
                          ['Consumo Mensal', editing.cliente_consumo ? `R$ ${Number(editing.cliente_consumo).toLocaleString('pt-BR', { minimumFractionDigits:2 })}` : null],
                          ['Cidade',       [editing.cliente_cidade, editing.cliente_estado].filter(Boolean).join(' / ')],
                          ['Endereço',     editing.cliente_endereco],
                        ].filter(([, v]) => v).map(([k, v]) => (
                          <div key={k}>
                            <div style={{ fontSize:10, color:'#475569', textTransform:'uppercase', letterSpacing:0.5 }}>{k}</div>
                            <div style={{ fontSize:12, color:'#e2e8f0', fontWeight:500 }}>{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Documentos e arquivos (somente visualização para vendedor) ── */}
                  {isVendedor() && (
                    <div style={{ background:'rgba(30,42,64,0.6)', border:'1px solid #1a2035', borderRadius:10, padding:14, marginBottom:14 }}>
                      <div style={{ fontSize:11, color:'#60a5fa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📄 Documentos e Arquivos</div>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                        {[
                          ['📋 Proposta PDF',        editing.proposta_pdf_url],
                          ['📝 Contrato PDF',        editing.contrato_pdf_url],
                          ['✅ Contrato Assinado',   editing.contrato_assinado_url],
                          ['📎 Prova de Venda',      editing.prova_venda_url],
                          ['🔑 Procuração (CEMIG)',  editing.cemig_assinado_url],
                          ['🧾 Boleto',              editing.boleto_url],
                        ].map(([label, url]) => url ? (
                          <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                            style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600, color:'#60a5fa', background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.3)', borderRadius:7, padding:'6px 12px', textDecoration:'none' }}>
                            {label}
                          </a>
                        ) : null)}
                        {![editing.proposta_pdf_url, editing.contrato_pdf_url, editing.contrato_assinado_url, editing.prova_venda_url, editing.cemig_assinado_url, editing.boleto_url].some(Boolean) && (
                          <span style={{ fontSize:12, color:'#475569' }}>Nenhum documento disponível</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );

              /* ── CASE 5: Finalizado ── */
              if (st === 'finalizado') return <>
                <div style={{ background:'rgba(74,222,128,0.06)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:12, color:'#4ade80', fontWeight:700, marginBottom:6 }}>✅ Cliente Finalizado</div>
                  {editing?.data_conclusao && <div style={{ fontSize:12, color:'#94a3b8' }}>Concluído em: {fmtD ? fmtD(editing.data_conclusao) : editing.data_conclusao}</div>}
                  {editing?.observacoes && <div style={{ fontSize:12, color:'#94a3b8', marginTop:4 }}>Obs: {editing.observacoes}</div>}
                </div>
              </>;

              // Fallback: nenhum case bateu — mostra status para debug e opções básicas
              return (
                <>
                  <label style={S.label}>O que aconteceu com este cliente?</label>
                  <select style={S.inputSel} value={form.novoStatus || ''} onChange={e => setForm(p => ({ ...p, novoStatus: e.target.value }))}>
                    <option value="">— Selecione —</option>
                    <option value="visita">Visita Agendada</option>
                    <option value="em_espera">Aguardando Cliente</option>
                    <option value="perdido">Desistiu da Compra</option>
                  </select>
                  {form.novoStatus === 'visita' && (
                    <div>
                      <label style={S.label}>Data da Visita</label>
                      <input style={S.input} type="datetime-local" value={form.data_visita || ''} onChange={f('data_visita')} />
                    </div>
                  )}
                  {form.novoStatus === 'em_espera' && (
                    <>
                      <label style={S.label}>O que está aguardando?</label>
                      <textarea style={{ ...S.input, height:70, resize:'vertical' }} value={form.motivo_espera || ''} onChange={f('motivo_espera')} />
                      <label style={S.label}>Data para eu retornar</label>
                      <input style={S.input} type="datetime-local" value={form.prazo_resposta_vendedor || ''} onChange={f('prazo_resposta_vendedor')} />
                    </>
                  )}
                  {form.novoStatus === 'perdido' && (
                    <>
                      <label style={S.label}>Motivo de desistência</label>
                      <textarea style={{ ...S.input, height:70, resize:'vertical' }} value={form.motivo_desistencia || ''} onChange={f('motivo_desistencia')} />
                    </>
                  )}
                </>
              );
            })()}

            {/* Campos Pós Venda — sem travas: qualquer status pode ser escolhido,
                e todos os campos/ações ficam disponíveis independente do status atual */}
            {isPosVenda() && (() => {
              const inst = editingInstalador;
              const instConcluido = inst?.status === 'instalacao_concluida';
              const instStatusLabel = INSTALADOR_STATUS.find(s => s.v === inst?.status)?.l || inst?.status || '—';

              return <>
                {/* Kit Solar — kit vendido */}
                {(editing.kit_equipamento || editing.kit_marca) && (
                  <div style={{ background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.2)', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
                    <div style={{ fontSize:10, color:'#f6c90e', textTransform:'uppercase', letterSpacing:0.8, fontWeight:700, marginBottom:6 }}>📦 Kit Solar</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:10, fontSize:13, color:'#e2e8f0' }}>
                      {editing.kit_geracao != null && <span style={{ background:'rgba(246,201,14,0.12)', color:'#f6c90e', border:'1px solid rgba(246,201,14,0.2)', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>{editing.kit_geracao} kWh</span>}
                      {editing.kit_marca && <span style={{ background:'rgba(167,139,250,0.12)', color:'#a78bfa', border:'1px solid rgba(167,139,250,0.25)', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>{editing.kit_marca}</span>}
                      {editing.kit_equipamento && <span style={{ color:'#cbd5e1' }}>{editing.kit_equipamento}</span>}
                      {editing.kit_quantidade != null && <span style={{ color:'#64748b', fontSize:12 }}>{editing.kit_quantidade}x {editing.kit_placas_w || '—'}Wp</span>}
                      {editing.kit_valor != null && <span style={{ color:'#4ade80', fontWeight:700, fontSize:12 }}>R$ {Number(editing.kit_valor).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>}
                    </div>
                  </div>
                )}

                {/* Status — livre, qualquer valor */}
                <label style={S.label}>Status</label>
                <select style={S.inputSel} value={form.status || editing.status} onChange={f('status')}>
                  {POS_VENDA_STATUS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>

                {/* Documentos e status do engenheiro — info de apoio (diagrama/parecer agora ficam no painel "Dados do Engenheiro" abaixo) */}
                <div style={{ marginTop:14, marginBottom:4 }}>
                  {[
                    { label: 'Contrato Assinado', url: editing.contrato_assinado_url },
                  ].map(({ label, url }) => (
                    <div key={label} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, padding:'7px 10px', borderRadius:7, background: url ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)', border: `1px solid ${url ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'}` }}>
                      <span style={{ fontSize:13 }}>{url ? '✅' : '❌'}</span>
                      <span style={{ fontSize:12, color: url ? '#4ade80' : '#f87171', fontWeight:600 }}>{label} {url ? 'disponível' : 'não disponível'}</span>
                      {url && <a href={url} target="_blank" rel="noopener noreferrer" style={{ marginLeft:'auto', color:'#60a5fa', fontSize:12, textDecoration:'none', fontWeight:600 }}>Ver →</a>}
                    </div>
                  ))}
                  <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:7, background:'rgba(96,165,250,0.06)', border:'1px solid rgba(96,165,250,0.2)' }}>
                    <span style={{ fontSize:12, color:'#94a3b8' }}>⚙️ Engenheiro:</span>
                    <span style={{ fontSize:12, color:'#e2e8f0', fontWeight:600 }}>{LEGACY_ENG_LABEL[editing.e_status] || editing.e_status || '—'}</span>
                  </div>
                </div>

                {/* Data da Entrega, Instalador Designado e Data Prevista da Instalação —
                    visíveis em qualquer status do pós-venda */}
                <div>
                  <label style={S.label}>Data da Entrega</label>
                  <input style={S.input} type="date" value={form.data_entrega || ''} onChange={f('data_entrega')} />
                </div>

                <div style={{ background:'rgba(96,165,250,0.06)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:10, padding:14, marginTop:12 }}>
                  <div style={{ fontSize:12, color:'#60a5fa', fontWeight:700, marginBottom:10 }}>🔧 Instalador</div>
                  <label style={S.label}>Instalador Designado</label>
                  <select style={S.inputSel} value={form.instalador_designado || ''} onChange={f('instalador_designado')}>
                    <option value="">Selecionar instalador...</option>
                    {instaladoresList.map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
                  </select>
                  <label style={{ ...S.label, marginTop:8 }}>Data Prevista da Instalação</label>
                  <input style={S.input} type="date" value={form.data_prevista_instalacao || ''} onChange={f('data_prevista_instalacao')} />

                  {inst && (
                    <div style={{ marginTop:12 }}>
                      {[
                        ['Status do Instalador',    instStatusLabel],
                        ['Data de Início',          fmtDT(inst?.data_inicio)],
                        ['Data Prevista Conclusão', fmtD(inst?.data_prevista)],
                        ...(instConcluido ? [['Data Conclusão da Instalação', fmtDT(inst?.data_conclusao)]] : []),
                      ].map(([k, v]) => (
                        <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                          <span style={{ fontSize:12, color:'#475569' }}>{k}</span>
                          <span style={{ fontSize:12, color: instConcluido && k === 'Data Conclusão da Instalação' ? '#4ade80' : '#cbd5e1', fontWeight:600 }}>{v}</span>
                        </div>
                      ))}
                      {!instConcluido && (
                        <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:6, color:'#fb923c', fontSize:12 }}>
                          ⏳ Instalação não concluída
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Botão Gerar Documento Pós Obra — disponível em qualquer status */}
                <div style={{ marginTop:14, marginBottom:14 }}>
                  <button
                    style={{
                      display:'flex', alignItems:'center', gap:8,
                      padding:'10px 18px', borderRadius:8, cursor: gerandoPosObra ? 'not-allowed' : 'pointer',
                      background:'rgba(246,201,14,0.12)', border:'1px solid rgba(246,201,14,0.4)',
                      color:'#f6c90e', fontSize:13, fontWeight:700, width:'100%', justifyContent:'center',
                    }}
                    disabled={gerandoPosObra}
                    onClick={async () => {
                      setGerandoPosObra(true);
                      try {
                        const resp = await api.get(`/pos-venda/${editing.id}/gerar-pos-obra/`, { responseType:'blob' });
                        const url  = window.URL.createObjectURL(new Blob([resp.data], { type:'application/pdf' }));
                        const a    = document.createElement('a');
                        a.href     = url;
                        a.download = `Pos obra - ${editing.cliente_nome}.pdf`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch (e) {
                        alert('Erro ao gerar documento: ' + (e.message || 'tente novamente'));
                      } finally {
                        setGerandoPosObra(false);
                      }
                    }}
                  >
                    📄 {gerandoPosObra ? 'Gerando...' : 'Gerar Documento Pós Obra'}
                  </button>
                </div>

                {/* Finalização — sempre visível */}
                <div style={{ background:'rgba(74,222,128,0.06)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:12, color:'#4ade80', fontWeight:700, marginBottom:10 }}>✅ Finalização</div>
                  <label style={S.label}>Observações</label>
                  <textarea style={{ ...S.input, minHeight:60, resize:'vertical' }} value={form.observacoes || ''} onChange={f('observacoes')} />
                  {editing?.status === 'finalizado' && (
                    <div style={{ marginTop:8, fontSize:12, color:'#94a3b8' }}>Cliente já está finalizado.</div>
                  )}
                </div>

                {/* Trabalho do Engenheiro — acesso total igual engenheiro */}
                {editing?.engenheiro_id && (
                  <EngenheiroViewPosVenda engId={editing.engenheiro_id} />
                )}
              </>;
            })}

            {/* Campos Engenheiro */}
            {isEngenheiro() && <>
              {/* ── Ver Documentos ─────────────────────────────── */}
              {(() => {
                const docs = editing?.documentos_engenheiro;
                if (!docs) return null;

                const FileLink = ({ label, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display:'flex', alignItems:'center', gap:6, padding:'6px 10px',
                      borderRadius:7, fontSize:12, fontWeight:600, textDecoration:'none',
                      background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.3)',
                      color:'#60a5fa',
                    }}
                  >📄 {label}</a>
                );

                const ImgLink = ({ label, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display:'flex', alignItems:'center', gap:6, padding:'6px 10px',
                      borderRadius:7, fontSize:12, fontWeight:600, textDecoration:'none',
                      background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.25)',
                      color:'#4ade80',
                    }}
                  >🖼 {label}</a>
                );

                const MissingTag = ({ label }) => (
                  <span style={{
                    display:'flex', alignItems:'center', gap:6, padding:'6px 10px',
                    borderRadius:7, fontSize:12, background:'rgba(71,85,105,0.1)',
                    border:'1px solid rgba(71,85,105,0.2)', color:'#475569',
                  }}>— {label}</span>
                );

                const Secao = ({ titulo, itens, isImagem }) => {
                  const entries = Object.entries(itens);
                  if (entries.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize:10, color:'#475569', textTransform:'uppercase', letterSpacing:0.8, fontWeight:700, marginBottom:6 }}>{titulo}</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>
                        {entries.map(([label, href]) =>
                          href
                            ? isImagem
                              ? <ImgLink key={label} label={label} href={href} />
                              : <FileLink key={label} label={label} href={href} />
                            : <MissingTag key={label} label={label} />
                        )}
                      </div>
                    </div>
                  );
                };

                return (
                  <div style={{ marginBottom: 16 }}>
                    <button
                      type="button"
                      onClick={() => setShowDocs(v => !v)}
                      style={{
                        width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
                        padding:'10px 14px', borderRadius:9, cursor:'pointer',
                        background:'rgba(246,201,14,0.07)', border:'1px solid rgba(246,201,14,0.25)',
                        color:'#f6c90e', fontSize:13, fontWeight:700,
                      }}
                    >
                      <span>📁 Ver Documentos</span>
                      <span style={{ fontSize:11 }}>{showDocs ? '▲ Fechar' : '▼ Abrir'}</span>
                    </button>

                    {showDocs && (
                      <div style={{ marginTop:10, background:'rgba(0,0,0,0.2)', border:'1px solid #1a2035', borderRadius:10, padding:'14px 16px' }}>

                        {/* Kit */}
                        {docs.kit && (
                          <div style={{ marginBottom:14, background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.2)', borderRadius:8, padding:'10px 14px' }}>
                            <div style={{ fontSize:10, color:'#f6c90e', textTransform:'uppercase', letterSpacing:0.8, fontWeight:700, marginBottom:6 }}>Kit Solar</div>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:10, fontSize:13, color:'#e2e8f0' }}>
                              <span style={{ background:'rgba(246,201,14,0.12)', color:'#f6c90e', border:'1px solid rgba(246,201,14,0.2)', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>
                                {docs.kit.geracao} kWh
                              </span>
                              {docs.kit.marca && (
                                <span style={{ background:'rgba(167,139,250,0.12)', color:'#a78bfa', border:'1px solid rgba(167,139,250,0.25)', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>
                                  {docs.kit.marca}
                                </span>
                              )}
                              <span style={{ color:'#cbd5e1' }}>{docs.kit.equipamento}</span>
                              <span style={{ color:'#64748b', fontSize:12 }}>{docs.kit.quantidade}x {docs.kit.placas_w}Wp</span>
                              <span style={{ color:'#4ade80', fontWeight:700, fontSize:12 }}>
                                R$ {Number(docs.kit.valor).toLocaleString('pt-BR', { minimumFractionDigits:2 })}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Fotos do imóvel */}
                        <Secao titulo="Fotos do Imóvel" itens={docs.fotos || {}} isImagem={true} />

                        {/* Documentos do cliente */}
                        <Secao titulo="Documentos do Cliente" itens={docs.docs_cliente || {}} isImagem={false} />

                        {/* Arquivos do lead */}
                        <Secao titulo="Arquivos Gerados" itens={docs.arquivos_lead || {}} isImagem={false} />

                        {/* Dados do engenheiro */}
                        {Object.values(docs.dados_eng || {}).some(Boolean) && (
                          <Secao titulo="Dados do Engenheiro" itens={docs.dados_eng || {}} isImagem={false} />
                        )}

                        {/* Nenhum arquivo */}
                        {!docs.kit &&
                         !Object.values(docs.fotos || {}).some(Boolean) &&
                         !Object.values(docs.docs_cliente || {}).some(Boolean) &&
                         !Object.values(docs.arquivos_lead || {}).some(Boolean) &&
                         !Object.values(docs.dados_eng || {}).some(Boolean) && (
                          <div style={{ fontSize:13, color:'#475569', textAlign:'center', padding:'12px 0' }}>
                            Nenhum arquivo cadastrado ainda.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Status sequencial do engenheiro ── */}
              {(() => {
                const st = editing.status;
                const fmtDate = (d) => d ? new Date(d + (d.includes('T') ? '' : 'T12:00:00')).toLocaleDateString('pt-BR') : '—';
                const fmtDT2 = (d) => d ? new Date(d).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

                const StatusBadge = ({ st: s }) => {
                  const labels = { aguardando_engenheiro:'Aguardando Engenheiro', a_pagar_crt:'A Pagar CRT', analise_cemig:'Análise CEMIG', parecer_aprovado:'Parecer Aprovado', parecer_reprovado:'Parecer Reprovado', aguardando_solicitar_vistoria:'Aguardando Solicitar Vistoria', vistoria_solicitada:'Vistoria Solicitada', vistoria_concluida:'Vistoria Concluída' };
                  const colors = { aguardando_engenheiro:'#94a3b8', a_pagar_crt:'#fb923c', analise_cemig:'#60a5fa', parecer_aprovado:'#4ade80', parecer_reprovado:'#f87171', aguardando_solicitar_vistoria:'#a78bfa', vistoria_solicitada:'#f6c90e', vistoria_concluida:'#4ade80' };
                  const c = colors[s] || '#64748b';
                  return (
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderRadius:8, background:`${c}15`, border:`1px solid ${c}40`, marginBottom:12 }}>
                      <span style={{ width:8, height:8, borderRadius:'50%', background:c, flexShrink:0 }} />
                      <span style={{ fontSize:13, fontWeight:700, color:c }}>{labels[s] || s}</span>
                    </div>
                  );
                };

                const PanelBox = ({ color, children }) => (
                  <div style={{ background:`${color}08`, border:`1px solid ${color}25`, borderRadius:10, padding:14, marginTop:8 }}>
                    {children}
                  </div>
                );

                return <>
                  <label style={S.label}>Status atual</label>
                  <StatusBadge st={st} />

                  {/* Alterar status livremente — sem restrição de fluxo */}
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:16, background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.2)', borderRadius:8, padding:'8px 10px' }}>
                    <label style={{ fontSize:11, color:'#94a3b8', fontWeight:600, whiteSpace:'nowrap' }}>Alterar para qualquer status:</label>
                    <select
                      value={st}
                      disabled={savingEngStatus}
                      onChange={(e) => {
                        const novoStatus = e.target.value;
                        if (novoStatus === st) return;
                        const labels = { aguardando_engenheiro:'Aguardando Engenheiro', a_pagar_crt:'A Pagar CRT', analise_cemig:'Análise CEMIG', parecer_aprovado:'Parecer Aprovado', parecer_reprovado:'Parecer Reprovado', aguardando_solicitar_vistoria:'Aguardando Solicitar Vistoria', vistoria_solicitada:'Vistoria Solicitada', vistoria_concluida:'Vistoria Concluída' };
                        if (window.confirm(`Confirmar mudança de status para "${labels[novoStatus] || novoStatus}"?`)) {
                          definirStatusEngenheiro(novoStatus);
                        }
                      }}
                      style={{ ...S.inp, width:'auto', flex:'1 1 220px', padding:'6px 10px', fontSize:12 }}
                    >
                      {ENG_STATUS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                  </div>

                  {/* Procuração Assinada */}
                  {editing.cemig_assinado_url && (
                    <a
                      href={editing.cemig_assinado_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.3)', color:'#60a5fa', fontSize:12, fontWeight:600, textDecoration:'none', marginBottom:10 }}
                    >
                      📄 Ver Procuração Assinada
                    </a>
                  )}

                  {/* Boleto */}
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Boleto</div>
                    {editing.boleto_url ? (
                      <a
                        href={editing.boleto_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', color:'#4ade80', fontSize:12, fontWeight:600, textDecoration:'none' }}
                      >
                        📄 Ver Boleto
                      </a>
                    ) : (
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <input
                          ref={boletoInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => { setBoletoFile(e.target.files[0] || null); setBoletoError(''); }}
                          style={{ display:'none' }}
                        />
                        <button
                          type="button"
                          disabled={uploadingBoleto}
                          onClick={async () => {
                            if (!boletoFile) { boletoInputRef.current?.click(); return; }
                            setUploadingBoleto(true); setBoletoError('');
                            try {
                              const fd = new FormData();
                              fd.append('arquivo', boletoFile);
                              const r = await api.post(`/documentos/subir-boleto/${editing.lead}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              setEditing(prev => ({ ...prev, boleto_url: r.data.url }));
                              setBoletoFile(null);
                            } catch(e) {
                              setBoletoError(e.response?.data?.error || 'Erro ao enviar boleto');
                            } finally {
                              setUploadingBoleto(false);
                            }
                          }}
                          style={{ ...S.btn('#f6c90e'), padding:'7px 14px', fontSize:12, opacity: uploadingBoleto ? 0.5 : 1 }}
                        >
                          {uploadingBoleto ? 'Enviando...' : boletoFile ? `Enviar: ${boletoFile.name}` : '📎 Anexar Boleto'}
                        </button>
                      </div>
                    )}
                    {boletoError && (
                      <div style={{ fontSize:11, color:'#f87171', marginTop:4 }}>{boletoError}</div>
                    )}
                  </div>

                  {/* CRT */}
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>CRT</div>
                    {editing.crt_url ? (
                      <a
                        href={editing.crt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', color:'#4ade80', fontSize:12, fontWeight:600, textDecoration:'none' }}
                      >📄 Ver CRT</a>
                    ) : (
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <input
                          ref={crtInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => { setCrtFile(e.target.files[0] || null); setCrtError(''); }}
                          style={{ display:'none' }}
                        />
                        <button
                          type="button"
                          disabled={uploadingCrt}
                          onClick={async () => {
                            if (!crtFile) { crtInputRef.current?.click(); return; }
                            setUploadingCrt(true); setCrtError('');
                            try {
                              const fd = new FormData();
                              fd.append('arquivo', crtFile);
                              const r = await api.post(`/engenheiro/${editing.id}/subir-crt/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              setEditing(prev => ({ ...prev, crt_url: r.data.url }));
                              setCrtFile(null);
                            } catch(e) {
                              setCrtError(e.response?.data?.error || 'Erro ao enviar CRT');
                            } finally {
                              setUploadingCrt(false);
                            }
                          }}
                          style={{ ...S.btn('#f6c90e'), padding:'7px 14px', fontSize:12, opacity: uploadingCrt ? 0.5 : 1 }}
                        >
                          {uploadingCrt ? 'Enviando...' : crtFile ? `Enviar: ${crtFile.name}` : '📎 Anexar CRT'}
                        </button>
                      </div>
                    )}
                    {crtError && (
                      <div style={{ fontSize:11, color:'#f87171', marginTop:4 }}>{crtError}</div>
                    )}
                  </div>

                  {/* Erro de ação */}
                  {engStatusError && (
                    <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'8px 12px', marginBottom:10, fontSize:12, color:'#f87171' }}>
                      {engStatusError}
                    </div>
                  )}

                  {/* CASE: aguardando_engenheiro → avançar para a_pagar_crt */}
                  {st === 'aguardando_engenheiro' && (
                    <PanelBox color="#fb923c">
                      <div style={{ fontSize:12, color:'#fb923c', fontWeight:700, marginBottom:8 }}>🔶 Avançar para A Pagar CRT</div>
                      <label style={S.label}>Data prevista pagamento CRT</label>
                      <input style={S.input} type="date" key={form.data_crt_pagamento || 'k-crt'} defaultValue={form.data_crt_pagamento || ''} onBlur={f('data_crt_pagamento')} />
                      <button
                        type="button"
                        disabled={!form.data_crt_pagamento || savingEngStatus}
                        onClick={() => avancarEngStatus('a_pagar_crt', { data_crt_pagamento: form.data_crt_pagamento })}
                        style={{ ...S.btn('#fb923c'), marginTop:10, padding:'8px 18px', opacity: (!form.data_crt_pagamento || savingEngStatus) ? 0.5 : 1 }}
                      >{savingEngStatus ? 'Salvando...' : 'Avançar → A Pagar CRT'}</button>
                    </PanelBox>
                  )}

                  {/* CASE: a_pagar_crt — engenheiro informa data de retorno CEMIG e avança */}
                  {st === 'a_pagar_crt' && (
                    <PanelBox color="#60a5fa">
                      <div style={{ fontSize:12, color:'#60a5fa', fontWeight:700, marginBottom:8 }}>🔵 Análise CEMIG</div>
                      <label style={S.label}>Data provável retorno CEMIG</label>
                      <input style={S.input} type="date" key={form.data_analise_cemig_retorno || 'k-cemig'} defaultValue={form.data_analise_cemig_retorno || ''} onBlur={f('data_analise_cemig_retorno')} />
                      <button
                        type="button"
                        disabled={!form.data_analise_cemig_retorno || savingEngStatus}
                        onClick={() => avancarEngStatus('analise_cemig', { data_analise_cemig_retorno: form.data_analise_cemig_retorno })}
                        style={{ ...S.btn('#60a5fa'), marginTop:10, padding:'8px 18px', opacity: (!form.data_analise_cemig_retorno || savingEngStatus) ? 0.5 : 1 }}
                      >{savingEngStatus ? 'Salvando...' : 'Avançar → Análise CEMIG'}</button>
                    </PanelBox>
                  )}

                  {/* CASE: analise_cemig — mostra data retorno CEMIG e botões parecer */}
                  {st === 'analise_cemig' && (
                    <PanelBox color="#60a5fa">
                      <div style={{ fontSize:12, color:'#60a5fa', fontWeight:700, marginBottom:8 }}>🔵 Em Análise CEMIG</div>
                      {editing.data_analise_cemig_retorno && (
                        <div style={{ fontSize:13, color:'#cbd5e1', marginBottom:8 }}>Retorno previsto CEMIG: <strong>{fmtDate(editing.data_analise_cemig_retorno)}</strong></div>
                      )}
                      {/* Parecer de acesso */}
                      {editing.parecer_acesso_status ? (
                        <div style={{ marginTop:8, padding:'8px 12px', borderRadius:7, background: editing.parecer_acesso_status === 'aprovado' ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)', border:`1px solid ${editing.parecer_acesso_status === 'aprovado' ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)'}` }}>
                          <span style={{ fontSize:13, fontWeight:700, color: editing.parecer_acesso_status === 'aprovado' ? '#4ade80' : '#f87171' }}>
                            {editing.parecer_acesso_status === 'aprovado' ? '✅ Parecer Aprovado' : '❌ Parecer Reprovado'}
                          </span>
                          {editing.parecer_acesso_data && (
                            <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
                              Data parecer: {fmtDT2(editing.parecer_acesso_data)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize:12, color:'#94a3b8', marginBottom:8 }}>Definir parecer de acesso:</div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button type="button" disabled={savingEngStatus}
                              onClick={async () => {
                                setSavingEngStatus(true); setEngStatusError('');
                                try {
                                  await api.post(`/documentos/parecer-status/${editing.cliente}/`, { status: 'aprovado' });
                                  // Backend auto-avança status do engenheiro
                                  const r = await api.get(`/engenheiro/${editing.id}/`);
                                  setEditing(prev => ({ ...prev, ...r.data, parecer_acesso_status: 'aprovado', parecer_acesso_data: new Date().toISOString() }));
                                  fetch();
                                } catch(e) { setEngStatusError(e.response?.data?.error || 'Erro'); }
                                finally { setSavingEngStatus(false); }
                              }}
                              style={{ ...S.btn('#4ade80'), padding:'7px 14px', fontSize:12, opacity: savingEngStatus ? 0.5 : 1 }}>
                              ✅ Aprovado
                            </button>
                            <button type="button" disabled={savingEngStatus}
                              onClick={async () => {
                                setSavingEngStatus(true); setEngStatusError('');
                                try {
                                  await api.post(`/documentos/parecer-status/${editing.cliente}/`, { status: 'reprovado' });
                                  const r = await api.get(`/engenheiro/${editing.id}/`);
                                  setEditing(prev => ({ ...prev, ...r.data, parecer_acesso_status: 'reprovado', parecer_acesso_data: new Date().toISOString() }));
                                  fetch();
                                } catch(e) { setEngStatusError(e.response?.data?.error || 'Erro'); }
                                finally { setSavingEngStatus(false); }
                              }}
                              style={{ ...S.btn('#f87171'), padding:'7px 14px', fontSize:12, opacity: savingEngStatus ? 0.5 : 1 }}>
                              ❌ Reprovado
                            </button>
                          </div>
                        </>
                      )}
                    </PanelBox>
                  )}

                  {/* CASE: parecer_reprovado → voltar para analise_cemig */}
                  {st === 'parecer_reprovado' && (
                    <PanelBox color="#f87171">
                      <div style={{ fontSize:12, color:'#f87171', fontWeight:700, marginBottom:8 }}>❌ Parecer Reprovado</div>
                      {editing.parecer_acesso_data && (
                        <div style={{ fontSize:12, color:'#64748b', marginBottom:10 }}>Data parecer: {fmtDT2(editing.parecer_acesso_data)}</div>
                      )}
                      <div style={{ fontSize:12, color:'#94a3b8', marginBottom:10 }}>Corrija as pendências e volte para análise CEMIG para novo parecer.</div>
                      <button
                        type="button"
                        disabled={savingEngStatus}
                        onClick={() => avancarEngStatus('analise_cemig')}
                        style={{ ...S.btn('#60a5fa'), padding:'8px 16px', fontSize:12, opacity: savingEngStatus ? 0.5 : 1 }}
                      >{savingEngStatus ? 'Aguarde...' : '↩ Voltar para Análise CEMIG'}</button>
                    </PanelBox>
                  )}

                  {/* CASE: aguardando_solicitar_vistoria — mostra dados do instalador */}
                  {(st === 'aguardando_solicitar_vistoria' || st === 'parecer_aprovado') && (() => {
                    const instLabels = { aguardando_instalador:'Aguardando Instalador', instalacao_programada:'Instalação Programada', instalando:'Instalando', instalacao_concluida:'Instalação Concluída' };
                    const instColors = { aguardando_instalador:'#64748b', instalacao_programada:'#60a5fa', instalando:'#f59e0b', instalacao_concluida:'#4ade80' };
                    const instSt = editing.inst_status;
                    const instCor = instColors[instSt] || '#94a3b8';
                    const instLabel = instLabels[instSt] || instSt || '—';
                    const concluido = instSt === 'instalacao_concluida';
                    return (
                      <PanelBox color="#a78bfa">
                        <div style={{ fontSize:12, color:'#a78bfa', fontWeight:700, marginBottom:10 }}>✅ Parecer Aprovado — Aguardando Vistoria</div>

                        {/* Status instalador */}
                        <label style={S.label}>Status Instalador</label>
                        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 11px', borderRadius:8, background:`${instCor}15`, border:`1px solid ${instCor}40`, marginBottom:12 }}>
                          <span style={{ width:7, height:7, borderRadius:'50%', background:instCor, flexShrink:0 }} />
                          <span style={{ fontSize:13, fontWeight:600, color:instCor }}>{instLabel}</span>
                        </div>

                        {concluido ? (
                          <>
                            {editing.inst_data_conclusao && (
                              <div style={{ fontSize:13, color:'#cbd5e1', marginBottom:12 }}>
                                Data Conclusão Instalação: <strong>{fmtDT2(editing.inst_data_conclusao)}</strong>
                              </div>
                            )}
                            <button
                              type="button"
                              disabled={savingEngStatus}
                              onClick={() => avancarEngStatus('vistoria_solicitada')}
                              style={{ ...S.btn('#f6c90e'), padding:'8px 18px', fontSize:12, opacity: savingEngStatus ? 0.5 : 1 }}
                            >{savingEngStatus ? 'Salvando...' : 'Avançar → Vistoria Solicitada'}</button>
                          </>
                        ) : (
                          editing.inst_data_prevista && (
                            <div style={{ fontSize:13, color:'#94a3b8' }}>
                              Data Prevista Conclusão: <strong style={{ color:'#cbd5e1' }}>{fmtDate(editing.inst_data_prevista)}</strong>
                            </div>
                          )
                        )}
                      </PanelBox>
                    );
                  })()}

                  {/* CASE: vistoria_solicitada — engenheiro pode concluir com data */}
                  {st === 'vistoria_solicitada' && (
                    <PanelBox color="#f6c90e">
                      <div style={{ fontSize:12, color:'#f6c90e', fontWeight:700, marginBottom:8 }}>🔧 Vistoria Solicitada</div>
                      {editing.vistoria_data_hora && (
                        <div style={{ fontSize:13, color:'#cbd5e1', marginBottom:10 }}>
                          📅 Prazo vistoria: <strong>{fmtDT2(editing.vistoria_data_hora)}</strong>
                        </div>
                      )}
                      <label style={S.label}>Data e hora da conclusão da vistoria</label>
                      <input style={S.input} type="datetime-local" key={form.data_vistoria_concluida || 'k-vist'} defaultValue={form.data_vistoria_concluida || ''} onBlur={f('data_vistoria_concluida')} />
                      <button
                        type="button"
                        disabled={!form.data_vistoria_concluida || savingEngStatus}
                        onClick={() => avancarEngStatus('vistoria_concluida', { data_vistoria_concluida: form.data_vistoria_concluida })}
                        style={{ ...S.btn('#4ade80'), marginTop:10, padding:'8px 18px', opacity: (!form.data_vistoria_concluida || savingEngStatus) ? 0.5 : 1 }}
                      >{savingEngStatus ? 'Salvando...' : '✅ Marcar Vistoria Concluída'}</button>
                    </PanelBox>
                  )}

                  {/* CASE: vistoria_concluida — read only */}
                  {st === 'vistoria_concluida' && (
                    <PanelBox color="#4ade80">
                      <div style={{ fontSize:12, color:'#4ade80', fontWeight:700, marginBottom:6 }}>✅ Vistoria Concluída</div>
                      {editing.data_vistoria_concluida && (
                        <div style={{ fontSize:13, color:'#cbd5e1' }}>Concluída em: <strong>{fmtDT2(editing.data_vistoria_concluida)}</strong></div>
                      )}
                    </PanelBox>
                  )}
                </>;
              })()}

              {/* Nota do chefe — exibida apenas se existir */}
              {editing.nota_chefe && (
                <div style={{ background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.25)', borderRadius:10, padding:14, marginTop:16 }}>
                  <div style={{ fontSize:10, color:'#f6c90e', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:8 }}>📋 Nota do Chefe</div>
                  <div style={{ fontSize:13, color:'#e2e8f0', lineHeight:1.6, marginBottom:10 }}>{editing.nota_chefe}</div>
                  <div style={{ display:'flex', gap:12, fontSize:11, color:'#64748b' }}>
                    <span>👤 {editing.nota_chefe_autor_nome || '—'}</span>
                    <span>📅 {editing.nota_chefe_data
                      ? new Date(editing.nota_chefe_data).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })
                      : '—'
                    }</span>
                  </div>
                </div>
              )}
            </>}

            {/* Campos Instalador — fluxo por etapa */}
            {isInstalador() && (() => {
              const st = editing.status;

              /* ── CASE 1: Aguardando → Instalação Programada ──
                 Data Prevista é definida pelo Pós Venda; o instalador confirma
                 (mantém a data) ou altera (define uma nova data). ── */
              if (st === 'aguardando_instalador') return <>
                <label style={S.label}>Status</label>
                <select style={S.inputSel} value={form.status || 'aguardando_instalador'} onChange={e => { f('status')(e); setConfirmarPrevisao(null); }}>
                  <option value="aguardando_instalador">Aguardando Instalador</option>
                  <option value="instalacao_programada">Instalação Programada</option>
                </select>
                {form.status === 'instalacao_programada' && (
                  editing.data_prevista ? (
                    <div style={{ background:'rgba(96,165,250,0.06)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:10, padding:14, marginTop:12 }}>
                      <div style={{ fontSize:12, color:'#60a5fa', fontWeight:700, marginBottom:8 }}>📅 Data Prevista definida pelo Pós Venda</div>
                      <div style={{ fontSize:14, color:'#e2e8f0', fontWeight:600, marginBottom:12 }}>
                        {new Date(editing.data_prevista + 'T12:00:00').toLocaleDateString('pt-BR')}
                      </div>
                      {confirmarPrevisao === null && (
                        <div style={{ display:'flex', gap:8 }}>
                          <button type="button" style={{ ...S.btn('#4ade80'), padding:'7px 14px' }} onClick={() => setConfirmarPrevisao('confirmado')}>Confirmar</button>
                          <button type="button" style={{ ...S.btn('#fb923c'), padding:'7px 14px' }} onClick={() => setConfirmarPrevisao('alterar')}>Alterar</button>
                        </div>
                      )}
                      {confirmarPrevisao === 'confirmado' && (
                        <div style={{ color:'#4ade80', fontSize:12, fontWeight:600 }}>✅ Data Prevista confirmada</div>
                      )}
                      {confirmarPrevisao === 'alterar' && <>
                        <label style={{ ...S.label, marginTop:8 }}>Nova Data Prevista</label>
                        <input style={S.input} type="date" value={form.data_prevista || ''} onChange={f('data_prevista')} />
                      </>}
                    </div>
                  ) : <>
                    <label style={{ ...S.label, marginTop:8 }}>Data Prevista Conclusão Instalação</label>
                    <input style={S.input} type="date" value={form.data_prevista || ''} onChange={f('data_prevista')} />
                  </>
                )}
              </>;

              /* ── CASE 2: Instalação Programada → Instalando ── */
              if (st === 'instalacao_programada') return <>
                <label style={S.label}>Status</label>
                <select style={S.inputSel} value={form.status || 'instalacao_programada'} onChange={e => { f('status')(e); setConfirmarPrevisao(null); }}>
                  <option value="instalacao_programada">Instalação Programada</option>
                  <option value="instalando">Instalando</option>
                </select>
                {form.status === 'instalando' && (
                  <div style={{ background:'rgba(96,165,250,0.06)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:10, padding:14, marginTop:12 }}>
                    <div style={{ fontSize:12, color:'#60a5fa', fontWeight:700, marginBottom:8 }}>📅 Data Prevista de Conclusão</div>
                    <div style={{ fontSize:14, color:'#e2e8f0', fontWeight:600, marginBottom:12 }}>
                      {editing.data_prevista ? new Date(editing.data_prevista + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                    </div>
                    {confirmarPrevisao === null && (
                      <div style={{ display:'flex', gap:8 }}>
                        <button type="button" style={{ ...S.btn('#4ade80'), padding:'7px 14px' }} onClick={() => setConfirmarPrevisao('confirmado')}>Confirmar</button>
                        <button type="button" style={{ ...S.btn('#fb923c'), padding:'7px 14px' }} onClick={() => setConfirmarPrevisao('alterar')}>Alterar</button>
                      </div>
                    )}
                    {confirmarPrevisao === 'confirmado' && (
                      <div style={{ color:'#4ade80', fontSize:12, fontWeight:600 }}>✅ Data de Previsão de Conclusão confirmada</div>
                    )}
                    {confirmarPrevisao === 'alterar' && <>
                      <label style={{ ...S.label, marginTop:8 }}>Nova Data Previsão Conclusão</label>
                      <input style={S.input} type="date" value={form.data_prevista || ''} onChange={f('data_prevista')} />
                    </>}
                  </div>
                )}
              </>;

              /* ── CASE 3: Instalando → Instalação Concluída ── */
              if (st === 'instalando') return <>
                {/* Parecer de acesso do engenheiro */}
                {editing.parecer_acesso_status && (
                  <div style={{ padding:'8px 12px', borderRadius:8, background: editing.parecer_acesso_status === 'aprovado' ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)', border:`1px solid ${editing.parecer_acesso_status === 'aprovado' ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'}`, marginBottom:10 }}>
                    <span style={{ fontSize:12, fontWeight:700, color: editing.parecer_acesso_status === 'aprovado' ? '#4ade80' : '#f87171' }}>
                      Parecer de acesso: {editing.parecer_acesso_status === 'aprovado' ? 'Aprovado' : 'Reprovado'}
                    </span>
                    {editing.parecer_acesso_data && (
                      <span style={{ fontSize:11, color:'#64748b', marginLeft:8 }}>
                        dia {new Date(editing.parecer_acesso_data).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </span>
                    )}
                  </div>
                )}
                <label style={S.label}>Status</label>
                <select style={S.inputSel} value={form.status || 'instalando'} onChange={f('status')}>
                  <option value="instalando">Instalando</option>
                  <option value="instalacao_concluida">Instalação Concluída</option>
                </select>
                {form.status === 'instalacao_concluida' && (
                  <div style={{ background:'rgba(74,222,128,0.06)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:14, marginTop:12 }}>
                    <div style={{ fontSize:12, color:'#4ade80', fontWeight:700, marginBottom:12 }}>✅ Instalação Concluída — preencha os dados finais</div>
                    <label style={S.label}>Data da Conclusão da Instalação</label>
                    <input style={S.input} type="datetime-local" value={form.data_conclusao || ''} onChange={f('data_conclusao')} />
                    <label style={{ ...S.label, marginTop:8 }}>Foto da Instalação</label>
                    {editing?.foto_url && !fotoFile && (
                      <div style={{ marginBottom:8 }}>
                        <a href={editing.foto_url} target="_blank" rel="noreferrer" style={{ color:'#4ade80', fontSize:12, display:'flex', alignItems:'center', gap:4 }}>
                          <Image size={13} /> Ver foto atual
                        </a>
                      </div>
                    )}
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:7, background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', color:'#4ade80', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        <Upload size={14} /> {fotoFile ? 'Trocar foto' : 'Selecionar foto'}
                        <input type="file" style={{ display:'none' }} onChange={e => setFotoFile(e.target.files[0])} accept="image/*" />
                      </label>
                      {fotoFile && <span style={{ fontSize:12, color:'#94a3b8' }}>{fotoFile.name}</span>}
                    </div>
                  </div>
                )}
              </>;

              /* ── CASE 5: Finalizado ── */
              if (st === 'finalizado') return <>
                <div style={{ background:'rgba(74,222,128,0.06)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:12, color:'#4ade80', fontWeight:700, marginBottom:6 }}>✅ Cliente Finalizado</div>
                  {editing?.data_conclusao && <div style={{ fontSize:12, color:'#94a3b8' }}>Concluído em: {fmtD ? fmtD(editing.data_conclusao) : editing.data_conclusao}</div>}
                  {editing?.observacoes && <div style={{ fontSize:12, color:'#94a3b8', marginTop:4 }}>Obs: {editing.observacoes}</div>}
                </div>
              </>;

              return null;
            })}

            {/* ── Fotos do Local (pré-obra) ───────────────────────────────────────── */}
            {(() => {
              // Upload: Vendedor, Chefe, GC, Engenheiro
              // Instalador e Pós Venda: somente visualização
              // Visibilidade: sempre (qualquer status)
              let leadId = null;
              let readonly = false;
              if (isVendedor()) {
                leadId = editing.id;
                if (editing.status === 'fechado') readonly = true;
              } else if (isEngenheiro()) {
                leadId = editing.lead;
              } else if (isChefe() || isGerenteComercial()) {
                leadId = editing.id;
              } else if (isPosVenda()) {
                leadId = editing.lead; readonly = true;
              } else if (isInstalador()) {
                leadId = editing.lead; readonly = true;
              }
              if (!leadId) return null;
              return <FotosLocalPanel key={leadId} leadId={leadId} readonly={readonly} />;
            })()}

            {/* ── Fotos da Instalação ─────────────────────────────────────────────── */}
            {(() => {
              // Só mostra quando há instalador atribuído (exceto o próprio instalador e engenheiro)
              let leadId = null;
              let readonly = false;
              if (isInstalador()) {
                leadId = editing.lead;
              } else if (isPosVenda()) {
                if (editing.lead) { leadId = editing.lead; }
              } else if (isChefe() || isGerenteComercial()) {
                if (editing.instalador_id) leadId = editing.id;
              } else if (isVendedor() && editing.instalador_id) {
                leadId = editing.id; readonly = true;
              } else if (isEngenheiro()) {
                leadId = editing.lead; readonly = true;
              }
              if (!leadId) return null;
              return <FotosInstalacaoPanel key={leadId} leadId={leadId} readonly={readonly} />;
            })()}

            {/* Dados do Engenheiro — chefe e engenheiro editam; pós venda e vendedor (venda fechada) só visualizam */}
            {editing?.cliente && (
              isEngenheiro() ||
              isPosVenda() ||
              (isVendedor() && editing?.status === 'fechado') ||
              ((isChefe() || isGerenteComercial()) && (
                editing?.status === 'fechado' ||
                ENG_STATUS.some(s => s.v === editing?.status) ||
                POS_VENDA_STATUS.some(s => s.v === editing?.status)
              ))
            ) && (
              <DadosEngenheiroPanel
                key={editing.cliente}
                clienteId={editing.cliente}
                readonly={!isChefe() && !isEngenheiro()}
              />
            )}

            {/* ── Tarefas pendentes do lead ── */}
            {(() => {
              // Para pós venda/engenheiro/instalador, editing.id é o PK do registro deles,
              // não do lead. O leadId correto nesses casos é editing.lead.
              const tarefaLeadId = (isPosVenda() || isEngenheiro() || isInstalador())
                ? editing?.lead
                : editing?.id;
              return tarefaLeadId ? (
                <div style={{ marginTop:20 }}>
                  <TarefasLeadPanel leadId={tarefaLeadId} />
                </div>
              ) : null;
            })()}

            {/* ── Conversas do lead ── */}
            {(isVendedor() || isChefe() || isGerenteComercial()) && editing?.id && (
              <div style={{ marginTop:12 }}>
                <ConversasPanel ref={conversasPanelRef} leadId={editing.id} readOnly={false} />
              </div>
            )}

            {mostrarCriarTarefa && (() => {
              const tarefaLeadId = (isPosVenda() || isEngenheiro() || isInstalador())
                ? editing?.lead
                : editing?.id;
              return tarefaLeadId ? (
                <CriarTarefaModal
                  leadIdInicial={tarefaLeadId}
                  clienteNomeInicial={editing.cliente_nome || ''}
                  onClose={() => setMostrarCriarTarefa(false)}
                  onSaved={() => { setMostrarCriarTarefa(false); }}
                />
              ) : null;
            })()}

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:16, flexWrap:'wrap' }}>
              {isChefe() && editing?.status === 'fechado' && (
                <button
                  style={{ padding:'9px 18px', borderRadius:8, background:'rgba(167,139,250,0.12)', border:'1px solid rgba(167,139,250,0.3)', color:'#a78bfa', cursor:'pointer', fontSize:13, fontWeight:700 }}
                  onClick={() => setVoltarFunilLead(editing)}
                >
                  ↩ Funil
                </button>
              )}
              <button
                style={{ padding:'9px 18px', borderRadius:8, background:'rgba(251,146,60,0.12)', border:'1px solid rgba(251,146,60,0.3)', color:'#fb923c', cursor:'pointer', fontSize:13, fontWeight:700 }}
                onClick={() => setMostrarCriarTarefa(true)}
              >
                + Criar tarefa
              </button>

              {/* Gerar Documento Pós Obra — Pós Venda, Chefe, Gerente Comercial */}
              {(isPosVenda() || isChefe() || isGerenteComercial()) && (() => {
                const posVendaId = isPosVenda() ? editing.id : editing.pos_venda_id;
                const instConcluida = editing.instalador_status === 'instalacao_concluida';
                const enabled = instConcluida && !!posVendaId;
                const handleGerarPosObra = async () => {
                  if (!enabled) return;
                  try {
                    const resp = await api.get(`/pos-venda/${posVendaId}/gerar-pos-obra/`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `pos_obra_${posVendaId}.pdf`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  } catch {
                    alert('Erro ao gerar documento pós obra.');
                  }
                };
                return (
                  <button
                    onClick={handleGerarPosObra}
                    disabled={!enabled}
                    title={!enabled ? 'Aguardando conclusão da instalação' : 'Gerar Documento Pós Obra'}
                    style={{
                      padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                      cursor: enabled ? 'pointer' : 'not-allowed',
                      background: enabled ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.1)',
                      border: `1px solid ${enabled ? 'rgba(34,197,94,0.35)' : 'rgba(100,116,139,0.2)'}`,
                      color: enabled ? '#22c55e' : '#475569',
                      transition: 'all 0.15s',
                    }}
                  >
                    📄 Pós Obra
                  </button>
                );
              })()}

              <button style={{ padding:'9px 18px', borderRadius:8, background:'#1a2035', border:'1px solid #2d3748', color:'#64748b', cursor:'pointer', fontSize:13 }} onClick={handleClose}>Cancelar</button>
              <button style={{ padding:'9px 18px', borderRadius:8, background:'rgba(246,201,14,0.15)', border:'1px solid rgba(246,201,14,0.3)', color:'#f6c90e', cursor:'pointer', fontSize:13, fontWeight:700 }} onClick={save} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Voltar para o Funil Comercial ── */}
      {voltarFunilLead && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setVoltarFunilLead(null); }}>
          <div style={{ ...S.modal, width:'min(400px,94vw)' }}>
            <div style={{ fontSize:16, fontWeight:800, color:'#a78bfa', marginBottom:12 }}>↩ Voltar para o Funil</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:6 }}>
              Cliente: <strong style={{ color:'#e2e8f0' }}>{voltarFunilLead.cliente_nome}</strong>
            </div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:20 }}>
              O lead voltará ao status <strong style={{ color:'#f6c90e' }}>Proposta</strong> e o vendedor poderá editar novamente.<br /><br />
              <strong style={{ color:'#f87171' }}>Atenção:</strong> os registros de Pós Venda, Engenheiro, Financeiro e Instalador vinculados a esta venda serão <strong style={{ color:'#f87171' }}>deletados</strong>.
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button
                style={{ padding:'9px 18px', borderRadius:8, background:'#1a2035', border:'1px solid #2d3748', color:'#64748b', cursor:'pointer', fontSize:13 }}
                onClick={() => setVoltarFunilLead(null)}
                disabled={voltandoFunil}
              >
                Cancelar
              </button>
              <button
                style={{ padding:'9px 18px', borderRadius:8, background:'rgba(167,139,250,0.15)', border:'1px solid rgba(167,139,250,0.4)', color:'#a78bfa', cursor: voltandoFunil ? 'not-allowed' : 'pointer', fontSize:13, fontWeight:700, opacity: voltandoFunil ? 0.6 : 1 }}
                onClick={() => voltarParaFunil(voltarFunilLead)}
                disabled={voltandoFunil}
              >
                {voltandoFunil ? 'Voltando...' : '↩ Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Enviar para Pré Venda ── */}
      {preVendaModal && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) { setPreVendaModal(null); setPreVendaNota(''); setPreVendaResponsavelId(''); } }}>
          <div style={{ ...S.modal, width:'min(440px,100%)' }}>
            <div style={S.modalTitle}>→ Enviar para Pré Venda</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
              Cliente: <strong style={{ color:'#e2e8f0' }}>{preVendaModal.cliente_nome}</strong>
            </div>

            {preVendaFuncionarios.length > 0 && (
              <>
                <label style={S.label}>Responsável Pré Venda (opcional)</label>
                <select
                  style={S.inputSel}
                  value={preVendaResponsavelId}
                  onChange={e => setPreVendaResponsavelId(e.target.value)}
                >
                  <option value="">— Sem responsável específico —</option>
                  {preVendaFuncionarios.map(f => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </select>
              </>
            )}

            <label style={S.label}>Motivo / Observação *</label>
            <textarea
              style={{ ...S.input, minHeight:90, resize:'vertical' }}
              placeholder="Descreva o motivo de retornar ao Pré Venda..."
              value={preVendaNota}
              onChange={e => setPreVendaNota(e.target.value)}
            />

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
              <button
                style={{ padding:'9px 18px', borderRadius:8, background:'#1a2035', border:'1px solid #2d3748', color:'#64748b', cursor:'pointer', fontSize:13 }}
                onClick={() => { setPreVendaModal(null); setPreVendaNota(''); setPreVendaResponsavelId(''); }}
              >Cancelar</button>
              <button
                style={{ padding:'9px 18px', borderRadius:8, background:'rgba(251,146,60,0.15)', border:'1px solid rgba(251,146,60,0.4)', color:'#fb923c', cursor:'pointer', fontSize:13, fontWeight:700 }}
                onClick={enviarPreVenda}
                disabled={savingPreVenda}
              >{savingPreVenda ? 'Enviando...' : 'Confirmar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* ── KitTableModal global ─────────────────────────────────────── */}
    {kitTableModal && (
      <KitTableModal
        kits={kitsLista}
        clienteNome={kitTableModal.clienteNome}
        onChoose={(kitId, kit) => kitTableModal.callback(kitId, kit)}
        onClose={() => setKitTableModal(null)}
      />
    )}
    {propostaVendaModal && editing && (
      <PropostaVendaModal
        lead={editing}
        kitsExternos={kitsLista}
        onSuccess={(updates) => setEditing(prev => ({ ...prev, ...updates }))}
        onClose={() => setPropostaVendaModal(false)}
      />
    )}
    </>
  );
}
